import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { PrimaryCamper, AgeCategory, YouthChildrenSubCategory, Dependant, CAMPERS_COLLECTION } from '../types/camper';
import { logAuthEvent } from '../utils/logging';
import { useIsMobile } from '../hooks/useIsMobile';
import '../components/Admin/CamperManagement/PrimaryCamperForm.css';
import './RegistrationMobile.css';

const MOBILE_FORM_STEPS = [
  'Contact',
  'Campers',
  'Accommodation',
  'Meals',
  'Other Fees',
  'Review & Submit'
] as const;

interface CampSettings {
  welcomeMessage: string;
  startDate: string;
  endDate: string;
  year: number;
  rates: {
    registration: {
      adult: { weekly: number; nightly: number };
      adultStudent: { weekly: number; nightly: number };
      youthAndChildren: { weekly: number; nightly: number };
    };
    meals?: {
      fullCampAdult: number;
      fullCampChild: number;
      dailyAdult: number;
      dailyChild: number;
    };
  };
}

const Registration: React.FC = () => {
  const { currentUser, isAdmin, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [currentStep, setCurrentStep] = useState(1);
  const [mobileFormStep, setMobileFormStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState<CampSettings | null>(null);
  const [profile, setProfile] = useState<PrimaryCamper | null>(null);
  const [adminMode, setAdminMode] = useState(false);
  const [targetCamperId, setTargetCamperId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState<PrimaryCamper | null>(null);
  const [editingField, setEditingField] = useState<{type: 'primary' | 'dependent', index: number, field: string} | null>(null);
  const [tempValue, setTempValue] = useState<string>('');
  const [isAddingNewCamper, setIsAddingNewCamper] = useState(false);
  const [newCamper, setNewCamper] = useState<{ FirstName: string; LastName: string; Age: AgeCategory; YouthChildrenCategory?: YouthChildrenSubCategory }>({ FirstName: '', LastName: '', Age: AgeCategory.Adult });
  const [showEditModal, setShowEditModal] = useState(false);
  const [arrivalDate, setArrivalDate] = useState<string>('');
  const [departureDate, setDepartureDate] = useState<string>('');
  const [accommodationPolicy, setAccommodationPolicy] = useState<string>('');
  const [accommodationFirstChoice, setAccommodationFirstChoice] = useState<string>('');
  const [accommodationSecondChoice, setAccommodationSecondChoice] = useState<string>('');
  const [campingType, setCampingType] = useState<string>('');
  const [vehicleSize, setVehicleSize] = useState<string>('');
  const [accommodationNotes, setAccommodationNotes] = useState<string>('');
  const [showAccommodationHistory, setShowAccommodationHistory] = useState<boolean>(false);
  const [showFeesModal, setShowFeesModal] = useState<boolean>(false);
  const [accommodationHistory, setAccommodationHistory] = useState<{[key: string]: string}>({});
  const [fullCampPassesAdult, setFullCampPassesAdult] = useState<number>(0);
  const [fullCampPassesChild, setFullCampPassesChild] = useState<number>(0);
  const [dailyPassesAdult, setDailyPassesAdult] = useState<number>(0);
  const [dailyPassesChild, setDailyPassesChild] = useState<number>(0);
  const [fridgeRental, setFridgeRental] = useState<string>('');
  const [donation, setDonation] = useState<string>('');
  const [donationAmount, setDonationAmount] = useState<number>(0);
  const [donationNote, setDonationNote] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingRegistration, setExistingRegistration] = useState<any>(null);
  const [showExistingRegChoice, setShowExistingRegChoice] = useState(false);
  const [editingRegistrationId, setEditingRegistrationId] = useState<string | null>(null);
  const [camperNights, setCamperNights] = useState<{[key: string]: number}>({});
  // Track which campers are selected to attend (primary camper key is 'primary', dependants are 'dependent-{index}')
  const [selectedCampers, setSelectedCampers] = useState<{[key: string]: boolean}>({ primary: true });
  
  // New profile form state (for users with no primaryCampers record)
  const [newProfile, setNewProfile] = useState({
    FirstName: '',
    LastName: '',
    Age: AgeCategory.Adult,
    email: currentUser?.email?.toLowerCase() || '',
    HomePhone: '',
    MobilePhone: '',
    Street: '',
    Town: '',
    Province: '',
    PostalCode: '',
    LicensePlate: ''
  });
  const [profileCreateError, setProfileCreateError] = useState('');
  const [creatingProfile, setCreatingProfile] = useState(false);
  const [verificationSyncPending, setVerificationSyncPending] = useState(false);
  const [verificationCheckKey, setVerificationCheckKey] = useState(0);
  const wizardStartLoggedRef = useRef(false);

  const normalizeOptionalString = (value?: string | null): string => {
    if (typeof value !== 'string') return '';
    return value.trim();
  };

  const getPreferredContactPhone = (camper: Partial<PrimaryCamper> | null | undefined): string => {
    if (!camper) return '';
    const homePhone = normalizeOptionalString(camper.HomePhone);
    const mobilePhone = normalizeOptionalString(camper.MobilePhone);
    return homePhone || mobilePhone;
  };

  const normalizeAgeCategory = (value: any): string => {
    return String(value || '')
      .trim()
      .replace(/\s+/g, '')
      .toLowerCase();
  };

  const isResolvedAgeCategory = (value: any): boolean => {
    const normalized = normalizeAgeCategory(value);
    return normalized === 'adult' || normalized === 'adultstudent' || normalized === 'youthchildren';
  };

  const hasValidYouthChildrenGradeLevel = (value: any): boolean => {
    const normalized = String(value || '').trim().toLowerCase();
    return normalized !== '' && normalized !== 'select grade level';
  };

  const handleCreateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    setProfileCreateError('');
    setCreatingProfile(true);

    try {
      await currentUser.reload();
      const tokenResult = await currentUser.getIdTokenResult(true);
      const hasVerifiedClaim = tokenResult.claims.email_verified === true;

      if (!hasVerifiedClaim) {
        setVerificationSyncPending(true);
        setProfileCreateError('Your email verification is still syncing. Please wait a few seconds and try again.');
        return;
      }

      setVerificationSyncPending(false);
      logAuthEvent({
        event: 'profile_create_started',
        email: currentUser.email?.toLowerCase() || '',
        userId: currentUser.uid,
        details: {
          route: '/register',
          attemptSource: 'profile_create_form'
        }
      }).catch(() => {});

      const docRef = await addDoc(collection(db, CAMPERS_COLLECTION), {
        FirstName: newProfile.FirstName.trim(),
        LastName: newProfile.LastName.trim(),
        Age: newProfile.Age,
        email: currentUser.email?.toLowerCase() || '',
        HomePhone: newProfile.HomePhone.trim(),
        MobilePhone: newProfile.MobilePhone.trim(),
        Street: newProfile.Street.trim(),
        Town: newProfile.Town.trim(),
        Province: newProfile.Province.trim(),
        PostalCode: newProfile.PostalCode.trim(),
        LicensePlate: newProfile.LicensePlate.trim(),
        loginExists: true,
        authUid: currentUser.uid,
        dependants: [],
        notes: [],
        deleted: false,
        createdDate: serverTimestamp(),
        lastModifiedDate: serverTimestamp()
      });

      setProfile({
        FirstName: newProfile.FirstName.trim(),
        LastName: newProfile.LastName.trim(),
        Age: newProfile.Age,
        email: currentUser.email?.toLowerCase() || '',
        HomePhone: newProfile.HomePhone.trim(),
        MobilePhone: newProfile.MobilePhone.trim(),
        Street: newProfile.Street.trim(),
        Town: newProfile.Town.trim(),
        Province: newProfile.Province.trim(),
        PostalCode: newProfile.PostalCode.trim(),
        LicensePlate: newProfile.LicensePlate.trim(),
        loginExists: true,
        authUid: currentUser.uid,
        dependants: [],
        notes: [],
        deleted: false,
        recordId: docRef.id,
        createdDate: new Date(),
        lastModifiedDate: new Date()
      });

      logAuthEvent({
        event: 'profile_create_success',
        email: currentUser.email?.toLowerCase() || '',
        userId: currentUser.uid,
        details: {
          route: '/register',
          camperId: docRef.id
        }
      }).catch(() => {});
    } catch (err: any) {
      console.error('Error creating profile:', err);
      logAuthEvent({
        event: 'primary_camper_create_failure',
        email: currentUser.email?.toLowerCase() || '',
        userId: currentUser.uid,
        error: err?.message || 'Unknown profile creation error',
        details: {
          errorCode: err?.code || 'unknown',
          route: '/register',
          emailVerified: currentUser.emailVerified,
          attemptSource: 'profile_create_form'
        }
      }).catch(() => {});
      logAuthEvent({
        event: 'profile_create_failure',
        email: currentUser.email?.toLowerCase() || '',
        userId: currentUser.uid,
        error: err?.message || 'Unknown profile creation error',
        details: {
          errorCode: err?.code || 'unknown',
          route: '/register',
          attemptSource: 'profile_create_form'
        }
      }).catch(() => {});

      if (err?.code === 'permission-denied') {
        setVerificationSyncPending(true);
        setProfileCreateError('Your email verification is still syncing. Please wait a few seconds and try again.');
      } else {
        setProfileCreateError('Failed to create your profile. Please try again.');
      }
    } finally {
      setCreatingProfile(false);
    }
  };

  const populateFromExistingRegistration = (reg: any) => {
    if (reg.campDates) {
      if (reg.campDates.arrivalDate) setArrivalDate(reg.campDates.arrivalDate);
      if (reg.campDates.departureDate) setDepartureDate(reg.campDates.departureDate);
    }
    if (reg.accommodation) {
      if (reg.accommodation.policy) setAccommodationPolicy(reg.accommodation.policy);
      if (reg.accommodation.firstChoice) setAccommodationFirstChoice(reg.accommodation.firstChoice);
      if (reg.accommodation.secondChoice) setAccommodationSecondChoice(reg.accommodation.secondChoice);
      if (reg.accommodation.campingType) setCampingType(reg.accommodation.campingType);
      if (reg.accommodation.vehicleSize) setVehicleSize(reg.accommodation.vehicleSize);
      if (reg.accommodation.notes) setAccommodationNotes(reg.accommodation.notes);
    }
    if (reg.financial?.otherFees?.meals) {
      const meals = reg.financial.otherFees.meals;
      setFullCampPassesAdult(meals.fullCampPassesAdult?.quantity || 0);
      setFullCampPassesChild(meals.fullCampPassesChild?.quantity || 0);
      setDailyPassesAdult(meals.dailyPassesAdult?.quantity || 0);
      setDailyPassesChild(meals.dailyPassesChild?.quantity || 0);
    }
    if (reg.financial?.otherFees?.fridge) {
      setFridgeRental(reg.financial.otherFees.fridge.requested ? 'yes' : 'no');
    }
    if (reg.financial?.otherFees?.donations) {
      const donationAmt = reg.financial.otherFees.donations.amount || 0;
      if (donationAmt > 0) {
        setDonation('yes');
        setDonationAmount(donationAmt);
        setDonationNote(reg.financial.otherFees.donations.note || '');
      } else {
        setDonation('no');
      }
    }
    if (reg.attendingCampers && Array.isArray(reg.attendingCampers) && profile) {
      const selections: { [key: string]: boolean } = {};
      const nights: { [key: string]: number } = {};
      const interests: { [key: string]: any } = {};

      for (const camper of reg.attendingCampers) {
        const fullName = `${camper.firstName} ${camper.lastName}`;
        if (fullName === `${profile.FirstName} ${profile.LastName}`) {
          selections['primary'] = true;
          nights['primary'] = camper.nights || 0;
          if (camper.programInterests) interests['primary'] = camper.programInterests;
        } else if (profile.dependants) {
          const depIndex = profile.dependants.findIndex(
            (d: any) => `${d.FirstName} ${d.LastName}` === fullName
          );
          if (depIndex >= 0) {
            selections[`dependent-${depIndex}`] = true;
            nights[`dependent-${depIndex}`] = camper.nights || 0;
            if (camper.programInterests) interests[`dependent-${depIndex}`] = camper.programInterests;
          }
        }
      }
      setSelectedCampers(selections);
      setCamperNights(nights);
      setProgramInterests(interests);
    }
  };

  // Track program interests for each camper (keyed by 'primary' or 'dependent-{index}')
  const [programInterests, setProgramInterests] = useState<{[key: string]: {
    nurseryProgram: boolean;
    vbsProgram: boolean;
    youthGroup: boolean;
    cruiseProgram: boolean;
    volunteerInterest: boolean;
  }}>({});

  // Toggle a program interest for a specific camper
  const toggleProgramInterest = (camperId: string, program: string) => {
    setProgramInterests(prev => ({
      ...prev,
      [camperId]: {
        nurseryProgram: prev[camperId]?.nurseryProgram || false,
        vbsProgram: prev[camperId]?.vbsProgram || false,
        youthGroup: prev[camperId]?.youthGroup || false,
        cruiseProgram: prev[camperId]?.cruiseProgram || false,
        volunteerInterest: prev[camperId]?.volunteerInterest || false,
        [program]: !prev[camperId]?.[program as keyof typeof prev[typeof camperId]]
      }
    }));
  };

  // Calculate nights based on arrival and departure dates
  const calculateNights = () => {
    if (!arrivalDate || !departureDate) return 0;
    const arrival = new Date(arrivalDate);
    const departure = new Date(departureDate);
    const timeDiff = departure.getTime() - arrival.getTime();
    const nights = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return nights > 0 ? nights : 0;
  };

  // Get nights for a specific camper (individual or calculated default)
  const getCamperNights = (camperId: string) => {
    return camperNights[camperId] || calculateNights();
  };

  // Update nights for a specific camper
  const updateCamperNights = (camperId: string, nights: number) => {
    setCamperNights(prev => ({
      ...prev,
      [camperId]: nights
    }));
  };

  // Check URL parameters for admin mode
  useEffect(() => {
    const camperId = searchParams.get('camperId');
    const adminModeParam = searchParams.get('adminMode');
    
    if (adminModeParam === 'true' && camperId && isAdmin) {
      setAdminMode(true);
      setTargetCamperId(camperId);
    }
  }, [searchParams, isAdmin]);

  const handleSubmitRegistration = async () => {
    if (!profile || !settings) return;
    
    setError('');
    setIsSubmitting(true);
    try {
      // Calculate registration fees based on selected attending campers
      const attendingCampers: Array<{FirstName: string; LastName: string; Age: AgeCategory; YouthChildrenCategory: any; camperId: string}> = [];
      
      // Include primary camper only if selected
      if (selectedCampers['primary']) {
        attendingCampers.push({
          FirstName: profile.FirstName,
          LastName: profile.LastName,
          Age: profile.Age,
          YouthChildrenCategory: null,
          camperId: 'primary'
        });
      }
      
      // Include only selected dependants
      (profile.dependants || []).forEach((dependant, index) => {
        if (selectedCampers[`dependent-${index}`]) {
          attendingCampers.push({
            FirstName: dependant.FirstName,
            LastName: dependant.LastName,
            Age: dependant.Age,
            YouthChildrenCategory: dependant.YouthChildrenCategory || null,
            camperId: `dependent-${index}`
          });
        }
      });
      
      // Validate that at least one camper is selected
      if (attendingCampers.length === 0) {
        setError('Please select at least one camper to attend');
        setIsSubmitting(false);
        return;
      }

      const unresolvedAgeCampers = attendingCampers.filter((camper) => !isResolvedAgeCategory(camper.Age));
      if (unresolvedAgeCampers.length > 0) {
        const unresolvedNames = unresolvedAgeCampers
          .map((camper) => `${camper.FirstName} ${camper.LastName}`.trim())
          .join(', ');
        const message = `Unable to submit. Update age category for: ${unresolvedNames}.`;
        setError(message);
        alert(message);
        setIsSubmitting(false);
        return;
      }

      const missingGradeLevelCampers = attendingCampers.filter((camper) =>
        normalizeAgeCategory(camper.Age) === 'youthchildren' &&
        !hasValidYouthChildrenGradeLevel(camper.YouthChildrenCategory)
      );
      if (missingGradeLevelCampers.length > 0) {
        const missingGradeNames = missingGradeLevelCampers
          .map((camper) => `${camper.FirstName} ${camper.LastName}`.trim())
          .join(', ');
        const message = `Unable to submit. Select a grade level for: ${missingGradeNames}.`;
        setError(message);
        alert(message);
        setIsSubmitting(false);
        return;
      }

      // Validate that accommodation policy is selected
      if (!accommodationPolicy) {
        setError('Please select your accommodation category');
        setIsSubmitting(false);
        return;
      }

      const contactPhone = getPreferredContactPhone(profile);
      if (!contactPhone) {
        setError('Please add a phone number to your profile before submitting. Click "Edit Profile" and add a Home or Mobile phone number.');
        setIsSubmitting(false);
        return;
      }
      const adults = attendingCampers.filter(dep => dep.Age === AgeCategory.Adult);
      const adultStudents = attendingCampers.filter(dep => dep.Age === AgeCategory.AdultStudent);
      const youthChildren = attendingCampers.filter(dep => dep.Age === AgeCategory.YouthChildren);

      // Calculate meal costs using rates from settings
      const fullCampAdultPrice = settings.rates?.meals?.fullCampAdult || 100;
      const fullCampChildPrice = settings.rates?.meals?.fullCampChild || 50;
      const dailyAdultPrice = settings.rates?.meals?.dailyAdult || 15;
      const dailyChildPrice = settings.rates?.meals?.dailyChild || 10;
      const totalMealCost = 
        (fullCampPassesAdult * fullCampAdultPrice) + 
        (fullCampPassesChild * fullCampChildPrice) + 
        (dailyPassesAdult * dailyAdultPrice) + 
        (dailyPassesChild * dailyChildPrice);

      const fridgeCost = fridgeRental === 'yes' ? 25.00 : 0;

      const registrationData = {
        // Keep top-level ownership for Firestore security rules
        userId: currentUser?.uid || '',
        // Basic registration info
        registrationInfo: {
          primaryCamperId: profile.recordId,
          primaryCamperName: `${profile.FirstName} ${profile.LastName}`,
          email: profile.email || '',
          phone: contactPhone,
          address: {
            street: profile.Street || '',
            town: profile.Town || '',
            province: profile.Province || '',
            postalCode: profile.PostalCode || ''
          },
          submittedAt: serverTimestamp(),
          timeSubmitted: new Date().toISOString(),
          submittedBy: currentUser?.uid || ''
        },

        // Attending campers
        attendingCampers: attendingCampers.map((camper) => {
          return {
            firstName: camper.FirstName,
            lastName: camper.LastName,
            age: camper.Age,
            YouthChildrenCategory: camper.YouthChildrenCategory || null,
            nights: getCamperNights(camper.camperId),
            programInterests: programInterests[camper.camperId] || {
              nurseryProgram: false,
              vbsProgram: false,
              youthGroup: false,
              cruiseProgram: false,
              volunteerInterest: false
            }
          };
        }),

        // Camp dates
        campDates: {
          arrivalDate,
          departureDate
        },

        // Accommodation
        accommodation: {
          policy: accommodationPolicy,
          firstChoice: accommodationFirstChoice,
          secondChoice: accommodationSecondChoice,
          campingType,
          vehicleSize: vehicleSize || null,
          notes: accommodationNotes
        },

        // Financial section
        financial: {
          registrationFees: {
            adults: adults.map(adult => {
              const nights = getCamperNights(adult.camperId);
              const isFullCamp = nights >= 5;
              const fee = isFullCamp 
                ? settings.rates.registration.adult.weekly 
                : settings.rates.registration.adult.nightly * nights;
              return {
                name: `${adult.FirstName} ${adult.LastName}`,
                fee: fee,
                nights: nights
              };
            }),
            adultStudents: adultStudents.map(student => {
              const nights = getCamperNights(student.camperId);
              const isFullCamp = nights >= 5;
              const fee = isFullCamp 
                ? settings.rates.registration.adultStudent.weekly 
                : settings.rates.registration.adultStudent.nightly * nights;
              return {
                name: `${student.FirstName} ${student.LastName}`,
                fee: fee,
                nights: nights
              };
            }),
            youthChildren: youthChildren.map(youth => {
              const nights = getCamperNights(youth.camperId);
              const isFullCamp = nights >= 5;
              const fee = isFullCamp 
                ? settings.rates.registration.youthAndChildren.weekly 
                : settings.rates.registration.youthAndChildren.nightly * nights;
              return {
                name: `${youth.FirstName} ${youth.LastName}`,
                fee: fee,
                nights: nights
              };
            }),
            totalRegistrationFees: [
              ...adults.map(adult => {
                const nights = getCamperNights(adult.camperId);
                const isFullCamp = nights >= 5;
                return isFullCamp 
                  ? settings.rates.registration.adult.weekly 
                  : settings.rates.registration.adult.nightly * nights;
              }),
              ...adultStudents.map(student => {
                const nights = getCamperNights(student.camperId);
                const isFullCamp = nights >= 5;
                return isFullCamp 
                  ? settings.rates.registration.adultStudent.weekly 
                  : settings.rates.registration.adultStudent.nightly * nights;
              }),
              ...youthChildren.map(youth => {
                const nights = getCamperNights(youth.camperId);
                const isFullCamp = nights >= 5;
                return isFullCamp 
                  ? settings.rates.registration.youthAndChildren.weekly 
                  : settings.rates.registration.youthAndChildren.nightly * nights;
              })
            ].reduce((sum, fee) => sum + fee, 0)
          },
          accommodationFees: {
            assignedAccommodation: null, // To be filled when assigned
            accommodationType: accommodationFirstChoice,
            accommodationFee: 0, // To be calculated when assigned
            notes: accommodationNotes
          },
          otherFees: {
            meals: {
              fullCampPassesAdult: {
                quantity: fullCampPassesAdult,
                unitPrice: fullCampAdultPrice,
                total: fullCampPassesAdult * fullCampAdultPrice
              },
              fullCampPassesChild: {
                quantity: fullCampPassesChild,
                unitPrice: fullCampChildPrice,
                total: fullCampPassesChild * fullCampChildPrice
              },
              dailyPassesAdult: {
                quantity: dailyPassesAdult,
                unitPrice: dailyAdultPrice,
                total: dailyPassesAdult * dailyAdultPrice
              },
              dailyPassesChild: {
                quantity: dailyPassesChild,
                unitPrice: dailyChildPrice,
                total: dailyPassesChild * dailyChildPrice
              },
              totalMealCost
            },
            fridge: {
              requested: fridgeRental === 'yes',
              cost: fridgeCost
            },
            donations: {
              amount: donation === 'yes' ? donationAmount : 0,
              note: donation === 'yes' ? donationNote.slice(0, 256) : ''
            },
            totalOtherFees: totalMealCost + fridgeCost + (donation === 'yes' ? donationAmount : 0)
          }
        },

        // Status
        status: 'Pending',
        year: settings.year
      };

      // Add or update Registrations collection
      let docRefId: string;
      if (editingRegistrationId) {
        await updateDoc(doc(db, 'Registrations', editingRegistrationId), registrationData);
        docRefId = editingRegistrationId;
        console.log('Registration updated with ID: ', docRefId);
      } else {
        const docRef = await addDoc(collection(db, 'Registrations'), registrationData);
        docRefId = docRef.id;
        console.log('Registration submitted with ID: ', docRefId);
      }

      logAuthEvent({
        event: 'registration_submitted_success',
        email: profile.email?.toLowerCase() || currentUser?.email?.toLowerCase() || '',
        userId: currentUser?.uid || '',
        details: {
          registrationId: docRefId,
          isUpdateAttempt: !!editingRegistrationId,
          adminMode: !!adminMode,
          year: settings.year
        }
      }).catch(() => {});
      
      // Send confirmation email (only for non-admin registrations)
      if (!adminMode && profile?.email) {
        try {
          // Format dates for display
          const formatDisplayDate = (dateStr: string) => {
            const date = new Date(dateStr + 'T12:00:00');
            return date.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            });
          };

          // Build camper list HTML
          const camperListHtml = attendingCampers.map(camper => {
            const categoryDisplay = camper.Age === 'AdultStudent' ? 'Adult Student' : 
                                   camper.Age === 'YouthChildren' ? 'Youth/Children' : 
                                   camper.Age;
            const nights = getCamperNights(camper.camperId);
            return `<tr>
              <td style="padding: 8px; border-bottom: 1px solid #eee;">${camper.FirstName} ${camper.LastName}</td>
              <td style="padding: 8px; border-bottom: 1px solid #eee;">${categoryDisplay}</td>
              <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">${nights} ${nights === 1 ? 'night' : 'nights'}</td>
            </tr>`;
          }).join('');

          // Build other items section
          const otherItems: string[] = [];
          if (fullCampPassesAdult > 0) otherItems.push(`${fullCampPassesAdult} Full Camp Meal Pass(es) - Adult`);
          if (fullCampPassesChild > 0) otherItems.push(`${fullCampPassesChild} Full Camp Meal Pass(es) - Child`);
          if (dailyPassesAdult > 0) otherItems.push(`${dailyPassesAdult} Daily Meal Pass(es) - Adult`);
          if (dailyPassesChild > 0) otherItems.push(`${dailyPassesChild} Daily Meal Pass(es) - Child`);
          if (fridgeRental === 'yes') otherItems.push('Fridge Rental');
          if (donation === 'yes' && donationAmount > 0) otherItems.push(`Donation: $${donationAmount.toFixed(2)}`);

          const otherItemsHtml = otherItems.length > 0 
            ? `<h3 style="color: #2c3e50; margin-top: 20px;">Additional Items Requested</h3>
               <ul style="margin: 0; padding-left: 20px;">
                 ${otherItems.map(item => `<li style="padding: 4px 0;">${item}</li>`).join('')}
               </ul>`
            : '';

          const confirmationEmailHtml = `
            <!DOCTYPE html>
            <html>
              <head>
                <meta charset="utf-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Registration Received - Berwick Camp ${settings.year}</title>
              </head>
              <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; background-color: #f5f5f5;">
                <div style="max-width: 600px; margin: 0 auto; padding: 20px; background-color: #ffffff;">
                  <h1 style="color: #2c3e50; border-bottom: 2px solid #4299e1; padding-bottom: 10px;">
                    Thank You for Registering for Berwick Camp ${settings.year}
                  </h1>
                  
                  <p>Camp this year will be held <strong>${formatDisplayDate(settings.startDate)} to ${formatDisplayDate(settings.endDate)}</strong>.</p>
                  
                  <p>We have received your registration for the following people:</p>
                  
                  <table style="width: 100%; border-collapse: collapse; margin: 15px 0;">
                    <thead>
                      <tr style="background-color: #f8f9fa;">
                        <th style="padding: 10px; text-align: left; border-bottom: 2px solid #ddd;">Name</th>
                        <th style="padding: 10px; text-align: left; border-bottom: 2px solid #ddd;">Category</th>
                        <th style="padding: 10px; text-align: center; border-bottom: 2px solid #ddd;">Nights</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${camperListHtml}
                    </tbody>
                  </table>

                  <p>We understand you are interested in <strong>${accommodationFirstChoice || 'accommodation (to be determined)'}</strong>.</p>

                  ${otherItemsHtml}

                  <div style="background-color: #fff3cd; border: 1px solid #ffc107; border-radius: 4px; padding: 15px; margin: 20px 0;">
                    <h3 style="color: #856404; margin-top: 0;">Important Information</h3>
                    <p style="margin-bottom: 10px;">Please note this email is an <strong>automated response</strong>. While it confirms that we have received your registration, <strong>it does not confirm that you have the accommodation you requested</strong>.</p>
                    <p style="margin-bottom: 10px;">Accommodation needs to be confirmed by the registrar (a human) and you will receive another email with your invoice once that is confirmed.</p>
                    <p style="margin-bottom: 0;"><strong>This email is not an invoice</strong> – it is only designed to let you know that we have received your request.</p>
                  </div>

                  <div style="background-color: #e7f3ff; border: 1px solid #4299e1; border-radius: 4px; padding: 15px; margin: 20px 0;">
                    <h3 style="color: #2c5aa0; margin-top: 0;">What Happens Next?</h3>
                    <p style="margin-bottom: 0;">The registrar will start assigning accommodations in <strong>May</strong>. You can expect your invoice and confirmation in that month.</p>
                  </div>

                  <hr style="border: 1px solid #eee; margin: 20px 0;">
                  
                  <div style="background-color: #f8f9fa; padding: 15px; border-radius: 4px;">
                    <p style="margin: 0; font-size: 13px; color: #666;">
                      <strong>Questions?</strong> This email is not monitored – please do not reply to this email. 
                      If you have questions, feel free to email us at <a href="mailto:registraruccma@gmail.com" style="color: #4299e1;">registraruccma@gmail.com</a>
                    </p>
                  </div>

                  <p style="margin-top: 30px; color: #666;">
                    Warm regards,<br>
                    <strong>Berwick Camp Automated Registrar</strong>
                  </p>
                </div>
              </body>
            </html>
          `;

          // Create email document in the emails collection
          await addDoc(collection(db, 'emails'), {
            to: [{ email: profile.email.toLowerCase() }],
            subject: `Registration Received - Berwick Camp ${settings.year}`,
            html: confirmationEmailHtml,
            status: 'pending',
            createdAt: serverTimestamp()
          });
          console.log('Confirmation email queued successfully');
        } catch (emailError) {
          // Log email error but don't fail the registration
          console.error('Failed to queue confirmation email:', emailError);
        }
      }
      
      navigate('/registration-success', {
        state: {
          camperName: `${profile.FirstName} ${profile.LastName}`,
          year: settings.year,
          isUpdate: !!editingRegistrationId
        },
        replace: true
      });
      
    } catch (error) {
      console.error('Error submitting registration: ', error);
      const submissionError = error as { code?: string; message?: string };
      const errorCode = submissionError.code || 'unknown';
      const errorMessage = submissionError.message || 'No error message';

      logAuthEvent({
        event: 'registration_submitted_failure',
        email: profile?.email?.toLowerCase() || currentUser?.email?.toLowerCase() || '',
        userId: currentUser?.uid || '',
        error: errorMessage,
        details: {
          errorCode,
          adminMode: !!adminMode,
          isUpdateAttempt: !!editingRegistrationId
        }
      }).catch(() => {});

      try {
        await addDoc(collection(db, 'submissionErrors'), {
          event: 'registration_submit_failed',
          errorCode,
          errorMessage,
          userId: currentUser?.uid || null,
          isAdmin: !!isAdmin,
          adminMode: !!adminMode,
          isUpdateAttempt: !!editingRegistrationId,
          clientTimestamp: new Date().toISOString(),
          timestamp: serverTimestamp()
        });
      } catch (logError) {
        // Never block the UI error path if logging also fails.
        console.error('Failed to log submission error: ', logError);
      }

      if (errorCode === 'permission-denied') {
        setError('Submission blocked by account permissions. Please contact the registrar if this continues.');
      } else {
        setError('Failed to submit registration. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser && !adminMode) return;

      try {
        setLoading(true);
        setError('');

        if (!adminMode && currentUser) {
          await currentUser.reload();
          const tokenResult = await currentUser.getIdTokenResult(true);
          const hasVerifiedClaim = tokenResult.claims.email_verified === true;
          setVerificationSyncPending(!hasVerifiedClaim);

          if (!hasVerifiedClaim) {
            setLoading(false);
            return;
          }
        } else {
          setVerificationSyncPending(false);
        }

        // Fetch camp settings
        const settingsRef = doc(db, 'settings', 'camp');
        const settingsSnap = await getDoc(settingsRef);
        
        if (settingsSnap.exists()) {
          const settingsData = settingsSnap.data();
          const newSettings = {
            welcomeMessage: settingsData.welcomeMessage || 'Welcome to Berwick Camp Registration!',
            startDate: settingsData.startDate || '',
            endDate: settingsData.endDate || '',
            year: settingsData.year || new Date().getFullYear(),
            rates: settingsData.rates || {
              registration: {
                adult: { weekly: 0, nightly: 0 },
                adultStudent: { weekly: 0, nightly: 0 },
                youthAndChildren: { weekly: 0, nightly: 0 }
              }
            }
          };
          setSettings(newSettings);
          
          // Set default arrival and departure dates
          if (settingsData.startDate && settingsData.endDate) {
            setArrivalDate(settingsData.startDate);
            setDepartureDate(settingsData.endDate);
          }
        }

        // Fetch user profile - either by camperId (admin mode) or email (normal mode)
        if (adminMode && targetCamperId) {
          // Admin mode: load specific camper by ID
          const camperDoc = await getDoc(doc(db, CAMPERS_COLLECTION, targetCamperId));
          
          if (camperDoc.exists()) {
            const profileData = camperDoc.data() as PrimaryCamper;
            setProfile({
              ...profileData,
              recordId: camperDoc.id,
              dependants: profileData.dependants || []
            });
          } else {
            setError('Camper not found');
          }
        } else if (currentUser) {
          // Normal mode: load by email
          const profileQuery = query(
            collection(db, CAMPERS_COLLECTION),
            where('email', '==', currentUser.email?.toLowerCase()),
            where('deleted', '!=', true)
          );
          const profileSnap = await getDocs(profileQuery);

          if (!profileSnap.empty) {
            const profileData = profileSnap.docs[0].data() as PrimaryCamper;
            setProfile({
              ...profileData,
              recordId: profileSnap.docs[0].id,
              dependants: profileData.dependants || []
            });
          }
        }

        // Check for existing registration this year (non-admin only)
        if (!adminMode && currentUser) {
          const settingsSnap2 = await getDoc(doc(db, 'settings', 'camp'));
          const campYear = settingsSnap2.exists() ? settingsSnap2.data().year : new Date().getFullYear();
          const existingRegQuery = query(
            collection(db, 'Registrations'),
            where('registrationInfo.submittedBy', '==', currentUser.uid),
            where('year', '==', campYear)
          );
          const existingRegSnap = await getDocs(existingRegQuery);
          if (!existingRegSnap.empty) {
            setExistingRegistration({ id: existingRegSnap.docs[0].id, ...existingRegSnap.docs[0].data() });
            setShowExistingRegChoice(true);
          }
        }

      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load registration data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, adminMode, targetCamperId, verificationCheckKey]);

  useEffect(() => {
    if (wizardStartLoggedRef.current || loading || adminMode || !profile || !currentUser) return;

    wizardStartLoggedRef.current = true;
    logAuthEvent({
      event: 'registration_wizard_started',
      email: currentUser.email?.toLowerCase() || profile.email?.toLowerCase() || '',
      userId: currentUser.uid,
      details: {
        route: '/register',
        currentStep,
        hasExistingRegistration: showExistingRegChoice
      }
    }).catch(() => {});
  }, [loading, adminMode, profile, currentUser, currentStep, showExistingRegChoice]);

  // Initialize selectedCampers when profile loads - preserve existing selections, default new entries to false
  useEffect(() => {
    if (profile && profile.dependants) {
      setSelectedCampers(prev => {
        const updated: {[key: string]: boolean} = { ...prev, primary: true };
        profile.dependants.forEach((_, index) => {
          const key = `dependent-${index}`;
          if (updated[key] === undefined) {
            updated[key] = false;
          }
        });
        return updated;
      });
    }
  }, [profile]);

  // Toggle camper selection
  const toggleCamperSelection = (camperId: string) => {
    setSelectedCampers(prev => ({
      ...prev,
      [camperId]: !prev[camperId]
    }));
  };

  const handleNext = () => {
    setMobileFormStep(0);
    setCurrentStep(prev => prev + 1);
  };

  const showMobileSection = (stepIndex: number) => !isMobile || mobileFormStep === stepIndex;

  const getMobileStepError = (stepIndex: number): string | null => {
    if (stepIndex === 1) {
      if (!arrivalDate || !departureDate) return 'Please select arrival and departure dates.';
      const dateError = validateDates(arrivalDate, departureDate);
      if (dateError) return dateError;
      const anySelected = Object.values(selectedCampers).some(Boolean);
      if (!anySelected) return 'Please select at least one camper to attend.';
      return null;
    }
    if (stepIndex === 2) {
      if (!accommodationPolicy) return 'Please select your accommodation category.';
      return null;
    }
    if (stepIndex === 4) {
      if (donation === 'yes' && (!donationAmount || donationAmount <= 0)) {
        return 'Please enter a donation amount, or choose No.';
      }
      return null;
    }
    return null;
  };

  const handleMobileNext = () => {
    const stepError = getMobileStepError(mobileFormStep);
    if (stepError) {
      setError(stepError);
      return;
    }
    setError('');
    setMobileFormStep((prev) => Math.min(prev + 1, MOBILE_FORM_STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleMobileBack = () => {
    setError('');
    if (mobileFormStep === 0) {
      setCurrentStep(1);
      return;
    }
    setMobileFormStep((prev) => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  // Helper function to generate date options between start and end dates
  const generateDateOptions = (startDate: string, endDate: string): string[] => {
    if (!startDate || !endDate) return [];
    
    const options: string[] = [];
    let currentDate = startDate;
    
    while (currentDate <= endDate) {
      options.push(currentDate);
      // Simple string manipulation to add one day
      const date = new Date(currentDate);
      date.setDate(date.getDate() + 1);
      currentDate = date.toISOString().split('T')[0];
    }
    
    return options;
  };

  // Helper function to format date for display
  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    // Parse as local date (no timezone conversion)
    const date = new Date(dateString + 'T12:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  };

  // Validation function for date selection
  const validateDates = (arrival: string, departure: string): string | null => {
    if (!arrival || !departure) return null;
    
    const arrivalDate = new Date(arrival);
    const departureDate = new Date(departure);
    
    if (arrivalDate > departureDate) {
      return 'Arrival date must be before or equal to departure date';
    }
    
    return null;
  };

  // Function to fetch accommodation history
  const fetchAccommodationHistory = async () => {
    if (!profile?.recordId) return;
    
    try {
      const camperRef = doc(db, CAMPERS_COLLECTION, profile.recordId);
      const camperDoc = await getDoc(camperRef);
      
      if (camperDoc.exists()) {
        const data = camperDoc.data();
        
        // Get accommodation data from the new accommodationHistory array
        const historyArray = data.accommodationHistory || [];
        const history: {[key: string]: string} = {};
        
        // Convert array to lookup object for easy access in the modal
        historyArray.forEach((entry: any) => {
          history[entry.year] = entry.placeCode || 'Did Not Attend';
        });
        
        setAccommodationHistory(history);
        setShowAccommodationHistory(true);
      }
    } catch (error) {
      console.error('Error fetching accommodation history:', error);
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Save changes
      handleSaveProfile();
    } else {
      // Start editing
      setEditedProfile(profile);
      setIsEditing(true);
    }
  };

  const handleSaveProfile = async () => {
    if (!editedProfile || !profile) return;

    try {
      setLoading(true);
      const profileRef = doc(db, CAMPERS_COLLECTION, profile.recordId);
      
      // Create update object with required fields
      const updateData: Partial<PrimaryCamper> = {
        FirstName: normalizeOptionalString(editedProfile.FirstName),
        LastName: normalizeOptionalString(editedProfile.LastName),
        HomePhone: normalizeOptionalString(editedProfile.HomePhone),
        Street: normalizeOptionalString(editedProfile.Street),
        Town: normalizeOptionalString(editedProfile.Town),
        Province: normalizeOptionalString(editedProfile.Province),
        PostalCode: normalizeOptionalString(editedProfile.PostalCode),
        lastModifiedDate: new Date()
      };

      // Only include optional fields if they have values
      const mobilePhone = normalizeOptionalString(editedProfile.MobilePhone);
      if (mobilePhone !== '') {
        updateData.MobilePhone = mobilePhone;
      }
      const licensePlate = normalizeOptionalString(editedProfile.LicensePlate);
      if (licensePlate !== '') {
        updateData.LicensePlate = licensePlate;
      }

      await updateDoc(profileRef, updateData);
      
      // Update local state
      setProfile({
        ...profile,
        ...updateData
      });
      
      setIsEditing(false);
      setEditedProfile(null);
      setError('');
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof PrimaryCamper, value: string) => {
    if (!editedProfile) return;
    setEditedProfile({
      ...editedProfile,
      [field]: value
    });
  };

  const handleFieldClick = (type: 'primary' | 'dependent', index: number, field: string, currentValue: string) => {
    setEditingField({ type, index, field });
    setTempValue(currentValue);
  };

  const stripUndefined = (obj: any) => JSON.parse(JSON.stringify(obj));

  const handleFieldSave = async (valueOverride?: string) => {
    if (!editingField || !profile) return;

    const saveValue = valueOverride !== undefined ? valueOverride : tempValue;

    try {
      setLoading(true);
      const profileRef = doc(db, CAMPERS_COLLECTION, profile.recordId);

      if (editingField.type === 'primary') {
        await updateDoc(profileRef, {
          [editingField.field]: saveValue,
          lastModifiedDate: new Date()
        });
        
        setProfile({
          ...profile,
          [editingField.field]: saveValue
        });
      } else {
        const updatedDependants = [...profile.dependants];
        const updatedEntry = {
          ...updatedDependants[editingField.index],
          [editingField.field]: saveValue
        };
        if (editingField.field === 'Age' && saveValue !== AgeCategory.YouthChildren) {
          delete (updatedEntry as any).YouthChildrenCategory;
        }
        updatedDependants[editingField.index] = updatedEntry;

        await updateDoc(profileRef, {
          dependants: stripUndefined(updatedDependants),
          lastModifiedDate: new Date()
        });

        setProfile({
          ...profile,
          dependants: updatedDependants
        });
      }

      setEditingField(null);
      setTempValue('');
      setError('');
    } catch (err) {
      console.error('Error updating field:', err);
      setError('Failed to update field');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldCancel = () => {
    setEditingField(null);
    setTempValue('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleFieldSave();
    } else if (e.key === 'Escape') {
      handleFieldCancel();
    }
  };

  const handleAddNewCamper = () => {
    setIsAddingNewCamper(true);
    setNewCamper({ FirstName: '', LastName: '', Age: AgeCategory.Adult });
  };

  const handleSaveNewCamper = async () => {
    if (!profile || !newCamper.FirstName.trim() || !newCamper.LastName.trim()) {
      setError('Please fill in both first and last name');
      return;
    }

    try {
      setLoading(true);
      const profileRef = doc(db, CAMPERS_COLLECTION, profile.recordId);
      
      const newDependant: Dependant = {
        FirstName: newCamper.FirstName.trim(),
        LastName: newCamper.LastName.trim(),
        Age: newCamper.Age,
        ...(newCamper.Age === AgeCategory.YouthChildren && newCamper.YouthChildrenCategory ? { YouthChildrenCategory: newCamper.YouthChildrenCategory } : {})
      };

      const updatedDependants = [...(profile.dependants || []), newDependant];

      await updateDoc(profileRef, {
        dependants: stripUndefined(updatedDependants),
        lastModifiedDate: new Date()
      });

      const newIndex = updatedDependants.length - 1;
      const newKey = `dependent-${newIndex}`;

      // Copy nights from the 'new-camper' temp key to the real key
      const newCamperNights = camperNights['new-camper'];
      if (newCamperNights !== undefined) {
        setCamperNights(prev => {
          const { 'new-camper': _, ...rest } = prev;
          return { ...rest, [newKey]: newCamperNights };
        });
      }

      // Auto-check the newly added camper
      setSelectedCampers(prev => ({ ...prev, [newKey]: true }));

      setProfile({
        ...profile,
        dependants: updatedDependants
      });

      setIsAddingNewCamper(false);
      setNewCamper({ FirstName: '', LastName: '', Age: AgeCategory.Adult });
      setError('');
    } catch (err) {
      console.error('Error adding new camper:', err);
      setError('Failed to add new camper');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelNewCamper = () => {
    setIsAddingNewCamper(false);
    setNewCamper({ FirstName: '', LastName: '', Age: AgeCategory.Adult });
  };

  const handleOpenEditModal = () => {
    setEditedProfile(profile);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditedProfile(null);
  };

  const handleSaveModalProfile = async () => {
    if (!editedProfile || !profile) return;

    try {
      setLoading(true);
      const profileRef = doc(db, CAMPERS_COLLECTION, profile.recordId);
      
      const updateData: Partial<PrimaryCamper> = {
        FirstName: normalizeOptionalString(editedProfile.FirstName),
        LastName: normalizeOptionalString(editedProfile.LastName),
        HomePhone: normalizeOptionalString(editedProfile.HomePhone),
        Street: normalizeOptionalString(editedProfile.Street),
        Town: normalizeOptionalString(editedProfile.Town),
        Province: normalizeOptionalString(editedProfile.Province),
        PostalCode: normalizeOptionalString(editedProfile.PostalCode),
        lastModifiedDate: new Date()
      };

      const mobilePhone = normalizeOptionalString(editedProfile.MobilePhone);
      if (mobilePhone !== '') {
        updateData.MobilePhone = mobilePhone;
      }
      const licensePlate = normalizeOptionalString(editedProfile.LicensePlate);
      if (licensePlate !== '') {
        updateData.LicensePlate = licensePlate;
      }

      await updateDoc(profileRef, updateData);
      
      setProfile({
        ...profile,
        ...updateData
      });
      
      setShowEditModal(false);
      setEditedProfile(null);
      setError('');
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDependent = async (index: number) => {
    if (!profile || !profile.dependants) return;
    
    if (window.confirm(`Are you sure you would like to remove ${profile.dependants[index].FirstName} ${profile.dependants[index].LastName} from our database?`)) {
      try {
        setLoading(true);
        const profileRef = doc(db, CAMPERS_COLLECTION, profile.recordId);
        
        const updatedDependants = profile.dependants.filter((_, i) => i !== index);

        await updateDoc(profileRef, {
          dependants: stripUndefined(updatedDependants),
          lastModifiedDate: new Date()
        });

        setProfile({
          ...profile,
          dependants: updatedDependants
        });

        setError('');
      } catch (err) {
        console.error('Error deleting dependent:', err);
        setError('Failed to delete camper');
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <div>
        <div className="registration-wizard">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={`registration-wizard${isMobile ? ' registration-wizard--mobile' : ''}`}>
        <div className="main-content">
          {adminMode && profile && (
            <div style={{
              backgroundColor: '#fff3cd',
              border: '2px solid #ffc107',
              borderRadius: '8px',
              padding: '1rem 1.5rem',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <span style={{ fontSize: '1.5em' }}>👤</span>
              <div>
                <strong style={{ fontSize: '1.1em', color: '#856404' }}>Admin Mode</strong>
                <p style={{ margin: '0.25rem 0 0 0', color: '#856404' }}>
                  Registering on behalf of: <strong>{profile.FirstName} {profile.LastName}</strong>
                </p>
              </div>
            </div>
          )}
          
          {error && <div className="error-message">{error}</div>}

          {!profile && verificationSyncPending && !adminMode && !loading && (
            <div className="contact-camper-form" style={{ padding: '2rem' }}>
              <h2>Finishing Account Setup</h2>
              <p style={{ color: '#555', marginBottom: '1.5rem' }}>
                Your account is signed in, but email verification is still syncing. This usually takes a few seconds.
              </p>
              <div className="form-actions">
                <button
                  type="button"
                  className="submit-button"
                  onClick={() => setVerificationCheckKey(prev => prev + 1)}
                  disabled={loading}
                >
                  {loading ? 'Checking...' : 'Check Again'}
                </button>
              </div>
            </div>
          )}

          {!profile && !verificationSyncPending && !adminMode && !loading && (
            <div className="contact-camper-form" style={{ padding: '2rem' }}>
              <h2>Create Your Camper Profile</h2>
              <p style={{ color: '#555', marginBottom: '1.5rem' }}>
                Before you can register, we need some basic contact information.
              </p>
              {profileCreateError && <div className="error-message">{profileCreateError}</div>}
              <form onSubmit={handleCreateProfile}>
                <div className="form-grid">
                  <div className="form-field">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={newProfile.FirstName}
                      onChange={(e) => setNewProfile(prev => ({ ...prev, FirstName: e.target.value }))}
                      required
                      disabled={creatingProfile}
                    />
                  </div>
                  <div className="form-field">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      value={newProfile.LastName}
                      onChange={(e) => setNewProfile(prev => ({ ...prev, LastName: e.target.value }))}
                      required
                      disabled={creatingProfile}
                    />
                  </div>
                  <div className="form-field">
                    <label>Age Category *</label>
                    <select
                      value={newProfile.Age}
                      onChange={(e) => setNewProfile(prev => ({ ...prev, Age: e.target.value as AgeCategory }))}
                      required
                      disabled={creatingProfile}
                    >
                      <option value={AgeCategory.Adult}>Adult</option>
                      <option value={AgeCategory.AdultStudent}>Adult Student</option>
                      <option value={AgeCategory.YouthChildren}>Youth/Children</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={newProfile.email}
                      readOnly
                      disabled
                      style={{ backgroundColor: '#f0f0f0' }}
                    />
                  </div>
                  <div className="form-field">
                    <label>Home Phone</label>
                    <input
                      type="tel"
                      value={newProfile.HomePhone}
                      onChange={(e) => setNewProfile(prev => ({ ...prev, HomePhone: e.target.value }))}
                      disabled={creatingProfile}
                    />
                  </div>
                  <div className="form-field">
                    <label>Mobile Phone *</label>
                    <input
                      type="tel"
                      value={newProfile.MobilePhone}
                      onChange={(e) => setNewProfile(prev => ({ ...prev, MobilePhone: e.target.value }))}
                      required
                      disabled={creatingProfile}
                    />
                  </div>
                  <div className="form-field full-width">
                    <label>Street Address *</label>
                    <input
                      type="text"
                      value={newProfile.Street}
                      onChange={(e) => setNewProfile(prev => ({ ...prev, Street: e.target.value }))}
                      required
                      disabled={creatingProfile}
                    />
                  </div>
                  <div className="form-field">
                    <label>Town *</label>
                    <input
                      type="text"
                      value={newProfile.Town}
                      onChange={(e) => setNewProfile(prev => ({ ...prev, Town: e.target.value }))}
                      required
                      disabled={creatingProfile}
                    />
                  </div>
                  <div className="form-field">
                    <label>Province *</label>
                    <input
                      type="text"
                      value={newProfile.Province}
                      onChange={(e) => setNewProfile(prev => ({ ...prev, Province: e.target.value }))}
                      required
                      disabled={creatingProfile}
                    />
                  </div>
                  <div className="form-field">
                    <label>Postal Code *</label>
                    <input
                      type="text"
                      value={newProfile.PostalCode}
                      onChange={(e) => setNewProfile(prev => ({ ...prev, PostalCode: e.target.value }))}
                      required
                      disabled={creatingProfile}
                    />
                  </div>
                  <div className="form-field">
                    <label>License Plate</label>
                    <input
                      type="text"
                      value={newProfile.LicensePlate}
                      onChange={(e) => setNewProfile(prev => ({ ...prev, LicensePlate: e.target.value }))}
                      disabled={creatingProfile}
                    />
                  </div>
                </div>
                <div className="form-actions">
                  <button type="submit" className="submit-button" disabled={creatingProfile}>
                    {creatingProfile ? 'Creating Profile...' : 'Create Profile & Continue'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Existing Registration Choice Screen */}
          {showExistingRegChoice && currentStep === 1 && settings && profile && (
            <div className="step-content welcome-step">
              <h1>Welcome Back, {profile.FirstName}!</h1>
              <div style={{
                backgroundColor: '#e7f3ff',
                border: '1px solid #4299e1',
                borderRadius: '8px',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                textAlign: 'center'
              }}>
                <p style={{ margin: 0, color: '#2c5aa0', fontSize: '1.1rem' }}>
                  We found an existing registration for <strong>Berwick Camp {settings.year}</strong> under your account.
                </p>
              </div>
              <p style={{ textAlign: 'center', color: '#555', marginBottom: '2rem' }}>
                What would you like to do?
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '400px', margin: '0 auto' }}>
                <button
                  onClick={() => {
                    if (existingRegistration.status === 'Assigned') {
                      navigate(`/invoice/${existingRegistration.id}`);
                      return;
                    }

                    setEditingRegistrationId(existingRegistration.id);
                    populateFromExistingRegistration(existingRegistration);
                    setShowExistingRegChoice(false);
                    setMobileFormStep(0);
                    setCurrentStep(2);
                  }}
                  style={{
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '1rem 1.5rem',
                    fontSize: '1rem',
                    cursor: 'pointer'
                  }}
                >
                  {existingRegistration.status === 'Assigned' ? 'Make a Payment' : 'Change My Existing Registration'}
                </button>
                <button
                  onClick={() => {
                    setShowExistingRegChoice(false);
                    setExistingRegistration(null);
                  }}
                  style={{
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '1rem 1.5rem',
                    fontSize: '1rem',
                    cursor: 'pointer'
                  }}
                >
                  Create an Additional Registration
                </button>
                <button
                  onClick={async () => {
                    await logout();
                    navigate('/login');
                  }}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '1rem 1.5rem',
                    fontSize: '1rem',
                    cursor: 'pointer'
                  }}
                >
                  Log Out
                </button>
              </div>
            </div>
          )}

          {/* Step 1: Welcome Message */}
          {currentStep === 1 && !showExistingRegChoice && settings && profile && (
            <div className="step-content welcome-step">
              <h1>Welcome to Camp Registration {settings.year}</h1>
              <div className="welcome-message">
                {settings.welcomeMessage}
              </div>
              <button onClick={handleNext} className="next-button">
                Continue to Registration
              </button>
            </div>
          )}

          {/* Step 2: Registration Form */}
          {currentStep === 2 && settings && profile && (
            <div>
              <h1 style={{ textAlign: 'center' }}>Berwick Camp Registration {settings.year}</h1>
              <p style={{ textAlign: 'center' }}>
                Camp runs from {new Date(settings.startDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} to {new Date(settings.endDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>

              {isMobile && (
                <div className="mobile-reg-progress">
                  <div className="mobile-reg-progress-label">
                    Step {mobileFormStep + 1} of {MOBILE_FORM_STEPS.length}
                  </div>
                  <div className="mobile-reg-progress-track" aria-hidden="true">
                    {MOBILE_FORM_STEPS.map((_, index) => (
                      <div
                        key={MOBILE_FORM_STEPS[index]}
                        className={`mobile-reg-progress-seg${
                          index < mobileFormStep ? ' is-done' : ''
                        }${index === mobileFormStep ? ' is-current' : ''}`}
                      />
                    ))}
                  </div>
                  <p className="mobile-reg-progress-title">{MOBILE_FORM_STEPS[mobileFormStep]}</p>
                </div>
              )}
              
              {/* Registration Information Box */}
              {showMobileSection(0) && (
              <div className="reg-section-contact" style={{ 
                marginTop: '1.5rem', 
                marginLeft: 'auto',
                marginRight: 'auto',
                width: 'fit-content',
                maxWidth: '600px',
                padding: '1rem 1.5rem', 
                border: '2px solid #007bff', 
                borderRadius: '8px', 
                backgroundColor: '#f8f9ff',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0, color: '#007bff', fontSize: '1.1rem', fontWeight: 'bold' }}>Registration Information</h3>
                  <button 
                    onClick={handleOpenEditModal}
                    style={{ 
                      backgroundColor: '#007bff', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      padding: '0.4rem 0.8rem',
                      cursor: 'pointer',
                      fontSize: '0.85rem'
                    }}
                  >
                    Edit
                  </button>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem 1rem', alignItems: 'center' }}>
                  <div style={{ fontWeight: 'bold', color: '#555', fontSize: '0.9rem', textAlign: 'right' }}>Contact Name:</div>
                  <div style={{ fontSize: '0.95rem', color: '#333' }}>{profile.FirstName} {profile.LastName}</div>
                  
                  <div style={{ fontWeight: 'bold', color: '#555', fontSize: '0.9rem', textAlign: 'right' }}>Telephone:</div>
                  <div style={{ fontSize: '0.95rem', color: '#333' }}>{profile.HomePhone || profile.MobilePhone || ''}</div>
                  
                  <div style={{ fontWeight: 'bold', color: '#555', fontSize: '0.9rem', textAlign: 'right' }}>Email:</div>
                  <div style={{ fontSize: '0.95rem', color: '#333' }}>{profile.email}</div>
                  
                  <div style={{ fontWeight: 'bold', color: '#555', fontSize: '0.9rem', textAlign: 'right' }}>Plate Number:</div>
                  <div style={{ fontSize: '0.95rem', color: '#333' }}>{profile.LicensePlate || ''}</div>
                  
                  <div style={{ fontWeight: 'bold', color: '#555', fontSize: '0.9rem', textAlign: 'right' }}>Address:</div>
                  <div style={{ fontSize: '0.95rem', color: '#333' }}>{profile.Street}, {profile.Town}, {profile.Province} {profile.PostalCode}</div>
                </div>
              </div>
              )}
              
              {/* Camper List Section */}
              {showMobileSection(1) && (
              <div className="reg-section-campers" style={{ marginTop: '2rem', padding: '1.5rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
                <h3 style={{ marginBottom: '1rem', color: '#333', fontSize: '1.1rem' }}>Attending Campers</h3>
                
                {/* Arrival and Departure Dates Section */}
                <div style={{ 
                  marginBottom: '1.5rem', 
                  padding: '1rem', 
                  border: '1px solid #ddd', 
                  borderRadius: '6px', 
                  backgroundColor: '#f9f9f9'
                }}>
                  <div className="reg-dates-row" style={{ display: 'flex', gap: '1rem', alignItems: 'end' }}>
                    {/* Arrival Date */}
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold', color: '#333', fontSize: '0.9rem' }}>
                        Arrival
                      </label>
                      <select
                        value={arrivalDate}
                        onChange={(e) => setArrivalDate(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="">Select date</option>
                        {settings && generateDateOptions(settings.startDate, settings.endDate).map(date => (
                          <option key={date} value={date}>
                            {formatDateForDisplay(date)}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Departure Date */}
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold', color: '#333', fontSize: '0.9rem' }}>
                        Departure
                      </label>
                      <select
                        value={departureDate}
                        onChange={(e) => setDepartureDate(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="">Select date</option>
                        {settings && generateDateOptions(settings.startDate, settings.endDate).map(date => (
                          <option key={date} value={date}>
                            {formatDateForDisplay(date)}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Reset Button */}
                    <div>
                      <button
                        onClick={() => {
                          if (settings) {
                            setArrivalDate(settings.startDate);
                            setDepartureDate(settings.endDate);
                          }
                        }}
                        style={{
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '0.5rem 0.75rem',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Reset
                      </button>
                    </div>
                  </div>
                  
                  {/* Validation Message */}
                  {validateDates(arrivalDate, departureDate) && (
                    <div style={{ 
                      marginTop: '0.75rem', 
                      padding: '0.5rem', 
                      backgroundColor: '#f8d7da', 
                      border: '1px solid #f5c6cb', 
                      borderRadius: '4px', 
                      color: '#721c24',
                      fontSize: '0.8rem',
                      textAlign: 'center'
                    }}>
                      ⚠️ {validateDates(arrivalDate, departureDate)}
                    </div>
                  )}
                </div>
                
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ marginBottom: '0.75rem', lineHeight: '1.4', fontSize: '0.95rem' }}>Please select and/or add all the people to be attending camp (including you).</p>
                  <p style={{ marginBottom: '0.75rem', lineHeight: '1.4', fontStyle: 'italic', color: '#666', fontSize: '0.9rem' }}>
                    💡 <strong>Tip:</strong> Click on any name or age category in the table below to edit it directly.
                  </p>
                {(() => {
                  const unresolvedSelectedCampers: string[] = [];
                  if (selectedCampers['primary'] && !isResolvedAgeCategory(profile.Age)) {
                    unresolvedSelectedCampers.push(`${profile.FirstName} ${profile.LastName}`.trim());
                  }
                  (profile.dependants || []).forEach((dependent, index) => {
                    if (selectedCampers[`dependent-${index}`] && !isResolvedAgeCategory(dependent.Age)) {
                      unresolvedSelectedCampers.push(`${dependent.FirstName} ${dependent.LastName}`.trim());
                    }
                  });
                  if (unresolvedSelectedCampers.length === 0) return null;

                  return (
                    <div style={{
                      marginTop: '0.75rem',
                      padding: '0.6rem 0.75rem',
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffe69c',
                      borderRadius: '4px',
                      color: '#664d03',
                      fontSize: '0.88rem'
                    }}>
                      <strong>Action required:</strong> Update age category for {unresolvedSelectedCampers.join(', ')} before submitting.
                    </div>
                  );
                })()}
                </div>
                
                {/* Attendees Table - Only show existing people */}
                <div className="reg-attendees-table" style={{ border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden' }}>
                  <div className="reg-attendees-header" style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr 1fr 80px 60px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #ddd' }}>
                    <div style={{ padding: '0.75rem', fontWeight: 'bold', borderRight: '1px solid #ddd', textAlign: 'center' }}>Attending</div>
                    <div style={{ padding: '0.75rem', fontWeight: 'bold', borderRight: '1px solid #ddd' }}>First Name</div>
                    <div style={{ padding: '0.75rem', fontWeight: 'bold', borderRight: '1px solid #ddd' }}>Last Name</div>
                    <div style={{ padding: '0.75rem', fontWeight: 'bold', borderRight: '1px solid #ddd' }}>Age Category</div>
                    <div style={{ padding: '0.75rem', fontWeight: 'bold', borderRight: '1px solid #ddd', textAlign: 'center' }}>Nights</div>
                    <div style={{ padding: '0.75rem', fontWeight: 'bold', textAlign: 'center' }}></div>
                  </div>
                  
                  {/* Primary Camper */}
                  <div className="reg-attendee-row" style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr 1fr 80px 60px', borderBottom: '1px solid #ddd' }}>
                    <div style={{ padding: '0.75rem', borderRight: '1px solid #ddd', textAlign: 'center' }}>
                      <input 
                        type="checkbox" 
                        checked={selectedCampers['primary'] ?? true}
                        onChange={() => toggleCamperSelection('primary')}
                      />
                    </div>
                    <div 
                      style={{ 
                        padding: '0.75rem', 
                        borderRight: '1px solid #ddd',
                        cursor: 'pointer',
                        minHeight: '2.5rem',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      onClick={() => handleFieldClick('primary', -1, 'FirstName', profile.FirstName)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {editingField?.type === 'primary' && editingField.field === 'FirstName' ? (
                        <input
                          type="text"
                          value={tempValue}
                          onChange={(e) => setTempValue(e.target.value)}
                          onKeyDown={handleKeyPress}
                          onBlur={() => handleFieldSave()}
                          autoFocus
                          style={{ width: '100%', border: '1px solid #007bff', borderRadius: '3px', padding: '0.25rem' }}
                        />
                      ) : (
                        <span style={{ width: '100%' }}>
                          {profile.FirstName}
                        </span>
                      )}
                    </div>
                    <div 
                      style={{ 
                        padding: '0.75rem', 
                        borderRight: '1px solid #ddd',
                        cursor: 'pointer',
                        minHeight: '2.5rem',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      onClick={() => handleFieldClick('primary', -1, 'LastName', profile.LastName)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {editingField?.type === 'primary' && editingField.field === 'LastName' ? (
                        <input
                          type="text"
                          value={tempValue}
                          onChange={(e) => setTempValue(e.target.value)}
                          onKeyDown={handleKeyPress}
                          onBlur={() => handleFieldSave()}
                          autoFocus
                          style={{ width: '100%', border: '1px solid #007bff', borderRadius: '3px', padding: '0.25rem' }}
                        />
                      ) : (
                        <span style={{ width: '100%' }}>
                          {profile.LastName}
                        </span>
                      )}
                    </div>
                    <div 
                      style={{ 
                        padding: '0.75rem',
                        cursor: 'pointer',
                        minHeight: '2.5rem',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      onClick={() => handleFieldClick('primary', -1, 'Age', profile.Age)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {editingField?.type === 'primary' && editingField.field === 'Age' ? (
                        <select
                          value={tempValue}
                          disabled={loading}
                          onChange={(e) => handleFieldSave(e.target.value)}
                          autoFocus
                          style={{ width: '100%', border: '1px solid #007bff', borderRadius: '3px', padding: '0.25rem' }}
                        >
                          <option value={AgeCategory.Adult}>Adult</option>
                          <option value={AgeCategory.AdultStudent}>Adult Student</option>
                          <option value={AgeCategory.YouthChildren}>Youth/Children</option>
                        </select>
                      ) : (
                        <span style={{ width: '100%' }}>
                          {profile.Age}
                        </span>
                      )}
                    </div>
                    <div style={{ padding: '0.75rem', borderRight: '1px solid #ddd', textAlign: 'center' }}>
                      <input
                        type="number"
                        min="0"
                        max="20"
                        value={getCamperNights('primary')}
                        onChange={(e) => updateCamperNights('primary', parseInt(e.target.value) || 0)}
                        style={{ width: '100%', padding: '0.25rem', border: '1px solid #ddd', borderRadius: '3px', textAlign: 'center' }}
                      />
                    </div>
                    <div style={{ padding: '0.75rem', textAlign: 'center' }}>
                      {/* No delete button for primary camper */}
                    </div>
                  </div>
                  
                  {/* Program Interest checkboxes for primary camper - only show when attending */}
                  {(selectedCampers['primary'] ?? true) && (
                    <div style={{ 
                      gridColumn: '1 / -1', 
                      backgroundColor: '#f0f7ff', 
                      padding: '0.5rem 1rem',
                      borderBottom: '1px solid #ddd',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '1rem',
                      fontSize: '0.85rem'
                    }}>
                      <span style={{ fontWeight: 'bold', marginRight: '0.5rem' }}>Program Interests:</span>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                        <input 
                          type="checkbox" 
                          checked={programInterests['primary']?.nurseryProgram || false}
                          onChange={() => toggleProgramInterest('primary', 'nurseryProgram')}
                        />
                        Nursery Program
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                        <input 
                          type="checkbox" 
                          checked={programInterests['primary']?.vbsProgram || false}
                          onChange={() => toggleProgramInterest('primary', 'vbsProgram')}
                        />
                        VBC Program
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                        <input 
                          type="checkbox" 
                          checked={programInterests['primary']?.youthGroup || false}
                          onChange={() => toggleProgramInterest('primary', 'youthGroup')}
                        />
                        Youth Group
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                        <input 
                          type="checkbox" 
                          checked={programInterests['primary']?.cruiseProgram || false}
                          onChange={() => toggleProgramInterest('primary', 'cruiseProgram')}
                        />
                        Cruise Program
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                        <input 
                          type="checkbox" 
                          checked={programInterests['primary']?.volunteerInterest || false}
                          onChange={() => toggleProgramInterest('primary', 'volunteerInterest')}
                        />
                        Volunteer Interest
                      </label>
                    </div>
                  )}
                  
                  {/* Dependents */}
                  {profile.dependants && profile.dependants.map((dependent, index) => {
                    return (
                      <React.Fragment key={index}>
                      <div className="reg-attendee-row" style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr 1fr 80px 60px', borderBottom: '1px solid #ddd' }}>
                        <div style={{ padding: '0.75rem', borderRight: '1px solid #ddd', textAlign: 'center' }}>
                          <input 
                            type="checkbox" 
                            checked={selectedCampers[`dependent-${index}`] ?? false}
                            onChange={() => toggleCamperSelection(`dependent-${index}`)}
                          />
                        </div>
                        <div 
                          style={{ 
                            padding: '0.75rem', 
                            borderRight: '1px solid #ddd',
                            cursor: 'pointer',
                            minHeight: '2.5rem',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          onClick={() => handleFieldClick('dependent', index, 'FirstName', dependent.FirstName)}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          {editingField?.type === 'dependent' && editingField.index === index && editingField.field === 'FirstName' ? (
                            <input
                              type="text"
                              value={tempValue}
                              onChange={(e) => setTempValue(e.target.value)}
                              onKeyDown={handleKeyPress}
                              onBlur={() => handleFieldSave()}
                              autoFocus
                              style={{ width: '100%', border: '1px solid #007bff', borderRadius: '3px', padding: '0.25rem' }}
                            />
                          ) : (
                            <span style={{ width: '100%' }}>
                              {dependent.FirstName}
                            </span>
                          )}
                        </div>
                        <div 
                          style={{ 
                            padding: '0.75rem', 
                            borderRight: '1px solid #ddd',
                            cursor: 'pointer',
                            minHeight: '2.5rem',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          onClick={() => handleFieldClick('dependent', index, 'LastName', dependent.LastName)}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          {editingField?.type === 'dependent' && editingField.index === index && editingField.field === 'LastName' ? (
                            <input
                              type="text"
                              value={tempValue}
                              onChange={(e) => setTempValue(e.target.value)}
                              onKeyDown={handleKeyPress}
                              onBlur={() => handleFieldSave()}
                              autoFocus
                              style={{ width: '100%', border: '1px solid #007bff', borderRadius: '3px', padding: '0.25rem' }}
                            />
                          ) : (
                            <span style={{ width: '100%' }}>
                              {dependent.LastName}
                            </span>
                          )}
                        </div>
                        <div 
                          style={{ 
                            padding: '0.75rem',
                            cursor: 'pointer',
                            minHeight: '2.5rem',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          onClick={() => handleFieldClick('dependent', index, 'Age', dependent.Age)}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          {editingField?.type === 'dependent' && editingField.index === index && editingField.field === 'Age' ? (
                            <select
                              value={tempValue}
                              disabled={loading}
                              onChange={(e) => handleFieldSave(e.target.value)}
                              autoFocus
                              style={{ width: '100%', border: '1px solid #007bff', borderRadius: '3px', padding: '0.25rem' }}
                            >
                              <option value={AgeCategory.Adult}>Adult</option>
                              <option value={AgeCategory.AdultStudent}>Adult Student</option>
                              <option value={AgeCategory.YouthChildren}>Youth/Children</option>
                            </select>
                          ) : (
                            <span style={{ width: '100%' }}>
                              {dependent.Age}
                              {dependent.Age === AgeCategory.YouthChildren && dependent.YouthChildrenCategory && (
                                <span style={{ fontSize: '0.8rem', color: '#666', marginLeft: '0.25rem' }}>
                                  ({dependent.YouthChildrenCategory})
                                </span>
                              )}
                            </span>
                          )}
                          {dependent.Age === AgeCategory.YouthChildren && (
                            <select
                              onClick={(e) => e.stopPropagation()}
                              value={dependent.YouthChildrenCategory || ''}
                              onChange={async (e) => {
                                const value = e.target.value as YouthChildrenSubCategory;
                                try {
                                  const updatedDependants = [...(profile?.dependants || [])];
                                  updatedDependants[index] = {
                                    ...updatedDependants[index],
                                    YouthChildrenCategory: value
                                  };
                                  const profileRef = doc(db, CAMPERS_COLLECTION, profile!.recordId);
                                  await updateDoc(profileRef, { dependants: stripUndefined(updatedDependants), lastModifiedDate: new Date() });
                                  setProfile({ ...profile!, dependants: updatedDependants });
                                } catch (err) {
                                  console.error('Error saving grade level:', err);
                                  setError('Failed to save grade level');
                                }
                              }}
                              style={{ width: '100%', border: '1px solid #007bff', borderRadius: '3px', padding: '0.25rem', marginTop: '0.25rem', fontSize: '0.85rem' }}
                            >
                              <option value="">Select Grade Level</option>
                              {Object.values(YouthChildrenSubCategory).map((cat) => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                            </select>
                          )}
                        </div>
                        <div style={{ padding: '0.75rem', borderRight: '1px solid #ddd', textAlign: 'center' }}>
                          <input
                            type="number"
                            min="0"
                            max="20"
                            value={getCamperNights(`dependent-${index}`)}
                            onChange={(e) => updateCamperNights(`dependent-${index}`, parseInt(e.target.value) || 0)}
                            style={{ width: '100%', padding: '0.25rem', border: '1px solid #ddd', borderRadius: '3px', textAlign: 'center' }}
                          />
                        </div>
                        <div style={{ padding: '0.75rem', textAlign: 'center' }}>
                          <button
                            onClick={() => handleDeleteDependent(index)}
                            style={{ 
                              backgroundColor: 'transparent', 
                              border: 'none', 
                              cursor: 'pointer',
                              fontSize: '1.2rem',
                              padding: '0.25rem'
                            }}
                            title="Delete camper"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      
                      {/* Program Interest checkboxes for dependent - only show when attending */}
                      {selectedCampers[`dependent-${index}`] && (
                        <div style={{ 
                          gridColumn: '1 / -1', 
                          backgroundColor: '#f0f7ff', 
                          padding: '0.5rem 1rem',
                          borderBottom: '1px solid #ddd',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '1rem',
                          fontSize: '0.85rem'
                        }}>
                          <span style={{ fontWeight: 'bold', marginRight: '0.5rem' }}>Program Interests:</span>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                            <input 
                              type="checkbox" 
                              checked={programInterests[`dependent-${index}`]?.nurseryProgram || false}
                              onChange={() => toggleProgramInterest(`dependent-${index}`, 'nurseryProgram')}
                            />
                            Nursery Program
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                            <input 
                              type="checkbox" 
                              checked={programInterests[`dependent-${index}`]?.vbsProgram || false}
                              onChange={() => toggleProgramInterest(`dependent-${index}`, 'vbsProgram')}
                            />
                            VBC Program
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                            <input 
                              type="checkbox" 
                              checked={programInterests[`dependent-${index}`]?.youthGroup || false}
                              onChange={() => toggleProgramInterest(`dependent-${index}`, 'youthGroup')}
                            />
                            Youth Group
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                            <input 
                              type="checkbox" 
                              checked={programInterests[`dependent-${index}`]?.cruiseProgram || false}
                              onChange={() => toggleProgramInterest(`dependent-${index}`, 'cruiseProgram')}
                            />
                            Cruise Program
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                            <input 
                              type="checkbox" 
                              checked={programInterests[`dependent-${index}`]?.volunteerInterest || false}
                              onChange={() => toggleProgramInterest(`dependent-${index}`, 'volunteerInterest')}
                            />
                            Volunteer Interest
                          </label>
                        </div>
                      )}
                    </React.Fragment>
                    );
                  })}
                  
                  {/* Add New Camper Row */}
                  {isAddingNewCamper ? (
                    <div className="reg-attendee-row" style={{ borderBottom: '1px solid #ddd', backgroundColor: '#f0f7ff' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr 1fr 80px 60px' }}>
                        <div style={{ padding: '0.75rem', borderRight: '1px solid #ddd', textAlign: 'center' }}>
                          <input type="checkbox" checked disabled title="Will be auto-selected on save" />
                        </div>
                        <div style={{ padding: '0.75rem', borderRight: '1px solid #ddd' }}>
                          <input
                            type="text"
                            placeholder="First Name"
                            value={newCamper.FirstName}
                            onChange={(e) => setNewCamper({ ...newCamper, FirstName: e.target.value })}
                            style={{ width: '100%', border: '1px solid #007bff', borderRadius: '3px', padding: '0.25rem' }}
                            autoFocus
                          />
                        </div>
                        <div style={{ padding: '0.75rem', borderRight: '1px solid #ddd' }}>
                          <input
                            type="text"
                            placeholder="Last Name"
                            value={newCamper.LastName}
                            onChange={(e) => setNewCamper({ ...newCamper, LastName: e.target.value })}
                            style={{ width: '100%', border: '1px solid #007bff', borderRadius: '3px', padding: '0.25rem' }}
                          />
                        </div>
                        <div style={{ padding: '0.75rem', borderRight: '1px solid #ddd' }}>
                          <select
                            value={newCamper.Age}
                            onChange={(e) => {
                              const age = e.target.value as AgeCategory;
                              setNewCamper({ ...newCamper, Age: age, YouthChildrenCategory: age === AgeCategory.YouthChildren ? newCamper.YouthChildrenCategory : undefined });
                            }}
                            style={{ width: '100%', border: '1px solid #007bff', borderRadius: '3px', padding: '0.25rem' }}
                          >
                            <option value={AgeCategory.Adult}>Adult</option>
                            <option value={AgeCategory.AdultStudent}>Adult Student</option>
                            <option value={AgeCategory.YouthChildren}>Youth/Children</option>
                          </select>
                          {newCamper.Age === AgeCategory.YouthChildren && (
                            <select
                              value={newCamper.YouthChildrenCategory || ''}
                              onChange={(e) => setNewCamper({ ...newCamper, YouthChildrenCategory: e.target.value as YouthChildrenSubCategory })}
                              style={{ width: '100%', border: '1px solid #007bff', borderRadius: '3px', padding: '0.25rem', marginTop: '0.25rem', fontSize: '0.85rem' }}
                            >
                              <option value="">Select Grade Level</option>
                              {Object.values(YouthChildrenSubCategory).map((cat) => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                            </select>
                          )}
                        </div>
                        <div style={{ padding: '0.75rem', borderRight: '1px solid #ddd', textAlign: 'center' }}>
                          <input
                            type="number"
                            min="0"
                            max="20"
                            value={getCamperNights('new-camper')}
                            onChange={(e) => updateCamperNights('new-camper', parseInt(e.target.value) || 0)}
                            style={{ width: '100%', padding: '0.25rem', border: '1px solid #007bff', borderRadius: '3px', textAlign: 'center' }}
                          />
                        </div>
                        <div style={{ padding: '0.75rem', textAlign: 'center' }}>
                          {/* Empty cell for actions column */}
                        </div>
                      </div>
                      <div style={{ padding: '0.5rem 0.75rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <button
                          onClick={handleSaveNewCamper}
                          style={{ 
                            backgroundColor: '#28a745', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '3px', 
                            padding: '0.4rem 1rem',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: 'bold'
                          }}
                        >
                          Save Camper
                        </button>
                        <button
                          onClick={handleCancelNewCamper}
                          style={{ 
                            backgroundColor: '#dc3545', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '3px', 
                            padding: '0.4rem 1rem',
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="reg-attendee-add-row"
                      style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '100px 1fr 1fr 1fr 80px 60px', 
                        borderTop: '2px solid #007bff',
                        backgroundColor: '#f8f9fa',
                        cursor: 'pointer'
                      }}
                      onClick={handleAddNewCamper}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                    >
                      <div style={{ padding: '0.75rem', borderRight: '1px solid #ddd', textAlign: 'center' }}>
                        <span style={{ fontSize: '1.2rem' }}>+</span>
                      </div>
                      <div style={{ padding: '0.75rem', borderRight: '1px solid #ddd', fontStyle: 'italic', color: '#007bff' }}>
                        Add New Camper
                      </div>
                      <div style={{ padding: '0.75rem', borderRight: '1px solid #ddd' }}>
                        {/* Empty cell */}
                      </div>
                      <div style={{ padding: '0.75rem', borderRight: '1px solid #ddd' }}>
                        {/* Empty cell for nights */}
                      </div>
                      <div style={{ padding: '0.75rem' }}>
                        {/* Empty cell */}
                      </div>
                      <div style={{ padding: '0.75rem', textAlign: 'center' }}>
                        {/* Empty cell for actions column */}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              )}
              
              
              {/* Accommodation Selection Section */}
              {showMobileSection(2) && (
              <div className="reg-section-accommodation" style={{ 
                marginTop: '1.5rem', 
                padding: '1.5rem', 
                border: '1px solid #ddd', 
                borderRadius: '8px', 
                backgroundColor: '#f9f9f9'
              }}>
                <h3 style={{ marginBottom: '0.75rem', color: '#333', fontSize: '1rem', textAlign: 'center' }}>Accommodation Selection</h3>
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <button
                    onClick={() => setShowFeesModal(true)}
                    style={{
                      backgroundColor: '#007bff',
                      color: 'white',
                      padding: '0.4rem 1rem',
                      borderRadius: '4px',
                      border: 'none',
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    View This Year's Fees
                  </button>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333', fontSize: '0.9rem' }}>
                    For the purposes of our accommodation Policy, I am: <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <select
                    value={accommodationPolicy}
                    onChange={(e) => setAccommodationPolicy(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="">Select your accommodation category</option>
                    <option value="program-staff">1. Program Staff</option>
                    <option value="returning-same-spot">2. Returning Camper who has been in the same spot for 2 or more years</option>
                    <option value="returning-no-fixed-address">3. Returning Camper with no fixed address</option>
                    <option value="returning-different-accommodations">4. Returning Camper wishing different accommodations</option>
                    <option value="new-camper">5. New Camper (have not attended Berwick Camp overnight in last 5 years)</option>
                    <option value="private-cottage-owner">6. Private Cottage Owner</option>
                    <option value="off-grounds">7. I don't need accommodation, will be staying off grounds</option>
                  </select>
                </div>
                
                {/* Accommodation Request Fields */}
                <div style={{ marginTop: '1.5rem' }}>
                  <div className="reg-accommodation-choices" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold', color: '#333', fontSize: '0.9rem' }}>
                        Accommodation Requested First Choice
                      </label>
                      <input
                        type="text"
                        value={accommodationFirstChoice}
                        onChange={(e) => setAccommodationFirstChoice(e.target.value)}
                        placeholder="Enter your first choice accommodation"
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                          backgroundColor: 'white'
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold', color: '#333', fontSize: '0.9rem' }}>
                        Accommodation Requested Second Choice
                      </label>
                      <input
                        type="text"
                        value={accommodationSecondChoice}
                        onChange={(e) => setAccommodationSecondChoice(e.target.value)}
                        placeholder="Enter your second choice accommodation"
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                          backgroundColor: 'white'
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="reg-camping-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    {/* Camping Type */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold', color: '#333', fontSize: '0.9rem' }}>
                        If Camping please indicate:
                      </label>
                      <select
                        value={campingType}
                        onChange={(e) => setCampingType(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="">Select camping type</option>
                        <option value="tent">Tent</option>
                        <option value="trailer">Trailer</option>
                        <option value="motorhome">MotorHome</option>
                      </select>
                    </div>
                    
                    {/* Vehicle Size - only show if Trailer or MotorHome selected */}
                    <div>
                      {(campingType === 'trailer' || campingType === 'motorhome') ? (
                        <>
                          <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold', color: '#333', fontSize: '0.9rem' }}>
                            Size in feet:
                          </label>
                          <select
                            value={vehicleSize}
                            onChange={(e) => setVehicleSize(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              fontSize: '0.9rem',
                              backgroundColor: 'white'
                            }}
                          >
                            <option value="">Select size</option>
                            <option value="under-20">Under 20 feet</option>
                            <option value="20-25">20-25 feet</option>
                            <option value="26-30">26-30 feet</option>
                            <option value="31-35">31-35 feet</option>
                            <option value="36-40">36-40 feet</option>
                            <option value="over-40">Over 40 feet</option>
                          </select>
                        </>
                      ) : (
                        <div style={{ height: '2.5rem' }}></div>
                      )}
                    </div>
                  </div>
                  
                  {/* Accommodation Notes */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold', color: '#333', fontSize: '0.9rem' }}>
                      Please put here anything you think the registrar should know when picking accommodations for you.
                    </label>
                    <textarea
                      value={accommodationNotes}
                      onChange={(e) => setAccommodationNotes(e.target.value)}
                      placeholder="Enter any additional information for the registrar..."
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '0.9rem',
                        backgroundColor: 'white',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                    />
                  </div>
                </div>
                
                {/* Accommodation History Link */}
                <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                  <button
                    onClick={fetchAccommodationHistory}
                    style={{
                      backgroundColor: 'transparent',
                      color: '#007bff',
                      border: 'none',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    View my accommodation History
                  </button>
                </div>
              </div>
              )}
              
              {/* Meals Section */}
              {showMobileSection(3) && (
              <div className="reg-section-meals" style={{ 
                marginTop: '1.5rem', 
                padding: '1.5rem', 
                border: '1px solid #ddd', 
                borderRadius: '8px', 
                backgroundColor: '#f9f9f9',
                maxWidth: '500px',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}>
                <h3 style={{ marginBottom: '1.5rem', color: '#333', fontSize: '1.1rem', textAlign: 'center' }}>Meals</h3>
                
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ marginBottom: '0.75rem', lineHeight: '1.4', fontSize: '0.95rem', textAlign: 'center' }}>
                    Select the number of meal passes you would like to purchase:
                  </p>
                </div>
                
                {/* Full Camp Pass - Adult */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', padding: '0.75rem', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #eee' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#333', fontSize: '0.9rem' }}>Full Camp Pass - Adult</div>
                    <div style={{ color: '#666', fontSize: '0.8rem' }}>${(settings?.rates?.meals?.fullCampAdult || 100).toFixed(2)} each</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      onClick={() => setFullCampPassesAdult(Math.max(0, fullCampPassesAdult - 1))}
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '0.25rem 0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        minWidth: '30px'
                      }}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      value={fullCampPassesAdult}
                      onChange={(e) => setFullCampPassesAdult(Math.max(0, parseInt(e.target.value) || 0))}
                      min="0"
                      style={{
                        width: '50px',
                        padding: '0.25rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        textAlign: 'center',
                        fontSize: '0.9rem'
                      }}
                    />
                    <button
                      onClick={() => setFullCampPassesAdult(fullCampPassesAdult + 1)}
                      style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '0.25rem 0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        minWidth: '30px'
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
                
                {/* Full Camp Pass - Child */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', padding: '0.75rem', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #eee' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#333', fontSize: '0.9rem' }}>Full Camp Pass - Child</div>
                    <div style={{ color: '#666', fontSize: '0.8rem' }}>${(settings?.rates?.meals?.fullCampChild || 50).toFixed(2)} each</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      onClick={() => setFullCampPassesChild(Math.max(0, fullCampPassesChild - 1))}
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '0.25rem 0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        minWidth: '30px'
                      }}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      value={fullCampPassesChild}
                      onChange={(e) => setFullCampPassesChild(Math.max(0, parseInt(e.target.value) || 0))}
                      min="0"
                      style={{
                        width: '50px',
                        padding: '0.25rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        textAlign: 'center',
                        fontSize: '0.9rem'
                      }}
                    />
                    <button
                      onClick={() => setFullCampPassesChild(fullCampPassesChild + 1)}
                      style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '0.25rem 0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        minWidth: '30px'
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
                
                {/* Daily Pass - Adult */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', padding: '0.75rem', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #eee' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#333', fontSize: '0.9rem' }}>Daily Pass - Adult</div>
                    <div style={{ color: '#666', fontSize: '0.8rem' }}>${(settings?.rates?.meals?.dailyAdult || 15).toFixed(2)} each</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      onClick={() => setDailyPassesAdult(Math.max(0, dailyPassesAdult - 1))}
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '0.25rem 0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        minWidth: '30px'
                      }}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      value={dailyPassesAdult}
                      onChange={(e) => setDailyPassesAdult(Math.max(0, parseInt(e.target.value) || 0))}
                      min="0"
                      style={{
                        width: '50px',
                        padding: '0.25rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        textAlign: 'center',
                        fontSize: '0.9rem'
                      }}
                    />
                    <button
                      onClick={() => setDailyPassesAdult(dailyPassesAdult + 1)}
                      style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '0.25rem 0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        minWidth: '30px'
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
                
                {/* Daily Pass - Child */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', padding: '0.75rem', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #eee' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#333', fontSize: '0.9rem' }}>Daily Pass - Child</div>
                    <div style={{ color: '#666', fontSize: '0.8rem' }}>${(settings?.rates?.meals?.dailyChild || 10).toFixed(2)} each</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      onClick={() => setDailyPassesChild(Math.max(0, dailyPassesChild - 1))}
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '0.25rem 0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        minWidth: '30px'
                      }}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      value={dailyPassesChild}
                      onChange={(e) => setDailyPassesChild(Math.max(0, parseInt(e.target.value) || 0))}
                      min="0"
                      style={{
                        width: '50px',
                        padding: '0.25rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        textAlign: 'center',
                        fontSize: '0.9rem'
                      }}
                    />
                    <button
                      onClick={() => setDailyPassesChild(dailyPassesChild + 1)}
                      style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '0.25rem 0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        minWidth: '30px'
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
                
                {/* Reset Button */}
                <div style={{ textAlign: 'center' }}>
                  <button
                    onClick={() => {
                      setFullCampPassesAdult(0);
                      setFullCampPassesChild(0);
                      setDailyPassesAdult(0);
                      setDailyPassesChild(0);
                    }}
                    style={{
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '0.5rem 1rem',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    Reset All to Zero
                  </button>
                </div>
              </div>
              )}

              {/* Other Fees Card */}
              {showMobileSection(4) && (
              <div className="reg-section-other" style={{
                marginTop: '1.5rem',
                padding: '1.5rem',
                border: '1px solid #ddd',
                borderRadius: '8px',
                backgroundColor: '#f9f9f9',
                maxWidth: '500px',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}>
                <h3 style={{ marginBottom: '1.5rem', color: '#333', fontSize: '1.1rem', textAlign: 'center' }}>Other Fees</h3>
                
                {/* Fridge Rental */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <label style={{ fontWeight: 'bold', color: '#333', fontSize: '0.9rem' }}>
                      Would you like to rent a fridge? (Note: Most cabins have fridges included)
                    </label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="fridgeRental"
                        value="yes"
                        checked={fridgeRental === 'yes'}
                        onChange={(e) => setFridgeRental(e.target.value)}
                        style={{ margin: 0 }}
                      />
                      <span>Yes</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="fridgeRental"
                        value="no"
                        checked={fridgeRental === 'no'}
                        onChange={(e) => setFridgeRental(e.target.value)}
                        style={{ margin: 0 }}
                      />
                      <span>No</span>
                    </label>
                  </div>
                  {fridgeRental === 'yes' && (
                    <div style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.8rem' }}>
                      Fridge rental cost will be determined from camp settings
                    </div>
                  )}
                </div>

                {/* Donation */}
                <div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <label style={{ fontWeight: 'bold', color: '#333', fontSize: '0.9rem' }}>
                      Would you like to make a donation?
                    </label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="donation"
                        value="yes"
                        checked={donation === 'yes'}
                        onChange={(e) => setDonation(e.target.value)}
                        style={{ margin: 0 }}
                      />
                      <span>Yes</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="donation"
                        value="no"
                        checked={donation === 'no'}
                        onChange={(e) => setDonation(e.target.value)}
                        style={{ margin: 0 }}
                      />
                      <span>No</span>
                    </label>
                  </div>
                  {donation === 'yes' && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', color: '#333' }}>
                        Donation Amount:
                      </label>
                      <div style={{ position: 'relative', width: '140px' }}>
                        <span style={{
                          position: 'absolute',
                          left: '0.5rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#333',
                          fontSize: '0.9rem',
                          pointerEvents: 'none'
                        }}>$</span>
                        <input
                          type="number"
                          value={donationAmount}
                          onChange={(e) => setDonationAmount(parseFloat(e.target.value) || 0)}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setDonationAmount(parseFloat(val.toFixed(2)));
                            e.target.value = val.toFixed(2);
                          }}
                          min="0"
                          step="1"
                          style={{
                            width: '100%',
                            padding: '0.5rem 0.5rem 0.5rem 1.25rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '0.9rem'
                          }}
                          placeholder="0.00"
                        />
                      </div>
                      <div style={{ marginTop: '0.75rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', color: '#666' }}>
                          Donation Note (optional, e.g., "In memory of..."):
                        </label>
                        <input
                          type="text"
                          value={donationNote}
                          onChange={(e) => setDonationNote(e.target.value.slice(0, 256))}
                          maxLength={256}
                          style={{
                            width: '100%',
                            maxWidth: '400px',
                            padding: '0.5rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '0.9rem'
                          }}
                          placeholder="Add a note to your donation"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Submit Registration Button */}
              {showMobileSection(5) && (
              <div className="reg-section-submit" style={{ 
                marginTop: '2rem', 
                textAlign: 'center',
                padding: '2rem',
                border: '2px solid #007bff',
                borderRadius: '8px',
                backgroundColor: '#f8f9ff'
              }}>
                <h3 style={{ marginBottom: '1rem', color: '#007bff', fontSize: '1.2rem' }}>
                  Ready to Submit Your Registration?
                </h3>
                <p style={{ marginBottom: '1.5rem', color: '#666', fontSize: '0.95rem' }}>
                  Please review all your information above before submitting. You can edit your profile information if needed.
                </p>
                <button
                  onClick={handleSubmitRegistration}
                  disabled={isSubmitting}
                  style={{
                    backgroundColor: isSubmitting ? '#6c757d' : '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '1rem 2rem',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    minWidth: '200px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    if (!isSubmitting) {
                      e.currentTarget.style.backgroundColor = '#0056b3';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!isSubmitting) {
                      e.currentTarget.style.backgroundColor = '#007bff';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Registration'}
                </button>
                {error && (
                  <div style={{ 
                    marginTop: '1rem', 
                    color: '#dc3545', 
                    fontSize: '0.9rem',
                    backgroundColor: '#f8d7da',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: '1px solid #f5c6cb'
                  }}>
                    {error}
                  </div>
                )}
              </div>
              )}

              {isMobile && (
                <div className="mobile-reg-nav">
                  <button type="button" className="mobile-reg-back" onClick={handleMobileBack}>
                    Back
                  </button>
                  {mobileFormStep < MOBILE_FORM_STEPS.length - 1 ? (
                    <button type="button" className="mobile-reg-next" onClick={handleMobileNext}>
                      Next
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Edit Profile Modal */}
      {showEditModal && editedProfile && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginBottom: '1.5rem', color: '#333' }}>Edit Profile Information</h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem' }}>First Name:</label>
                <input
                  type="text"
                  value={editedProfile.FirstName || ''}
                  onChange={(e) => handleInputChange('FirstName', e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem' }}>Last Name:</label>
                <input
                  type="text"
                  value={editedProfile.LastName || ''}
                  onChange={(e) => handleInputChange('LastName', e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem' }}>Home Phone:</label>
                <input
                  type="tel"
                  value={editedProfile.HomePhone || ''}
                  onChange={(e) => handleInputChange('HomePhone', e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem' }}>Mobile Phone:</label>
                <input
                  type="tel"
                  value={editedProfile.MobilePhone || ''}
                  onChange={(e) => handleInputChange('MobilePhone', e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem' }}>Street Address:</label>
                <input
                  type="text"
                  value={editedProfile.Street || ''}
                  onChange={(e) => handleInputChange('Street', e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem' }}>Town:</label>
                  <input
                    type="text"
                    value={editedProfile.Town || ''}
                    onChange={(e) => handleInputChange('Town', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem' }}>Province:</label>
                  <input
                    type="text"
                    value={editedProfile.Province || ''}
                    onChange={(e) => handleInputChange('Province', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
              </div>
              
              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem' }}>Postal Code:</label>
                <input
                  type="text"
                  value={editedProfile.PostalCode || ''}
                  onChange={(e) => handleInputChange('PostalCode', e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem' }}>License Plate:</label>
                <input
                  type="text"
                  value={editedProfile.LicensePlate || ''}
                  onChange={(e) => handleInputChange('LicensePlate', e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCloseEditModal}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveModalProfile}
                style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1rem',
                  cursor: 'pointer'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Fees Modal */}
      {showFeesModal && settings && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '650px',
            width: '90%',
            maxHeight: '85vh',
            overflowY: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: '#333' }}>Berwick Camp {settings.year} — Fee Schedule</h3>
              <button
                onClick={() => setShowFeesModal(false)}
                style={{ backgroundColor: 'transparent', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#999' }}
              >×</button>
            </div>

            {/* Registration Fees */}
            <h4 style={{ color: '#2c5aa0', borderBottom: '2px solid #4299e1', paddingBottom: '0.5rem', marginBottom: '0.75rem' }}>Registration Fees</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f0f4f8' }}>
                  <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem' }}>Category</th>
                  <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>Weekly</th>
                  <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>Nightly</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '0.5rem 0.75rem' }}>Adult</td>
                  <td style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>${settings.rates.registration.adult.weekly.toFixed(2)}</td>
                  <td style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>${settings.rates.registration.adult.nightly.toFixed(2)}</td>
                </tr>
                <tr style={{ backgroundColor: '#f9f9f9' }}>
                  <td style={{ padding: '0.5rem 0.75rem' }}>Adult Student</td>
                  <td style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>${settings.rates.registration.adultStudent.weekly.toFixed(2)}</td>
                  <td style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>${settings.rates.registration.adultStudent.nightly.toFixed(2)}</td>
                </tr>
                <tr>
                  <td style={{ padding: '0.5rem 0.75rem' }}>Youth / Children</td>
                  <td style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>${settings.rates.registration.youthAndChildren.weekly.toFixed(2)}</td>
                  <td style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>${settings.rates.registration.youthAndChildren.nightly.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>

            {/* Accommodation Fees */}
            {(settings.rates as any).accommodation && (
              <>
                <h4 style={{ color: '#2c5aa0', borderBottom: '2px solid #4299e1', paddingBottom: '0.5rem', marginBottom: '0.75rem' }}>Accommodation Fees</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f0f4f8' }}>
                      <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem' }}>Accommodation Type</th>
                      <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>Weekly</th>
                      <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>Nightly</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const accom = (settings.rates as any).accommodation;
                      const groups: { label: string; source: string; codes: string[]; noNightly?: boolean }[] = [
                        { label: 'Cottages', source: 'cottages', codes: ['C1', 'C2', 'C3'] },
                        { label: 'Private Cottages', source: 'cottages', codes: ['P1', 'P2', 'P3'], noNightly: true },
                        { label: 'Apartments', source: 'apartments', codes: ['A1', 'A2'] },
                        { label: 'Dorm / Fireside', source: 'dorm', codes: ['D1', 'D2'] },
                        { label: 'Camp Sites', source: 'campsite', codes: ['T1', 'T2'] }
                      ];
                      const codeLabels: { [key: string]: string } = {
                        C1: 'Full Service', C2: 'Partial Service', C3: 'No Service',
                        P1: 'Full Service', P2: 'Partial Service', P3: 'No Service',
                        A1: 'Full Service', A2: 'Partial Service',
                        D1: 'Single', D2: 'Double',
                        T1: 'Full Service', T2: 'Partial Service'
                      };
                      const rows: React.ReactElement[] = [];
                      let rowIndex = 0;
                      for (const group of groups) {
                        if (!accom[group.source]) continue;
                        const validCodes = group.codes.filter(code => {
                          const rate = accom[group.source][code];
                          return rate && (rate.weekly || rate.nightly);
                        });
                        if (validCodes.length === 0) continue;
                        rows.push(
                          <tr key={`header-${group.label}`}>
                            <td colSpan={3} style={{ padding: '0.6rem 0.75rem', fontWeight: 'bold', backgroundColor: '#e8eef4', color: '#2c5aa0', fontSize: '0.85rem' }}>
                              {group.label}
                            </td>
                          </tr>
                        );
                        for (const code of validCodes) {
                          const rate = accom[group.source][code];
                          rows.push(
                            <tr key={`${group.source}-${code}`} style={{ backgroundColor: rowIndex % 2 === 1 ? '#f9f9f9' : 'white' }}>
                              <td style={{ padding: '0.4rem 0.75rem 0.4rem 1.5rem' }}>{codeLabels[code] || code}</td>
                              <td style={{ textAlign: 'right', padding: '0.4rem 0.75rem' }}>${rate.weekly?.toFixed(2) || '0.00'}</td>
                              <td style={{ textAlign: 'right', padding: '0.4rem 0.75rem', color: group.noNightly ? '#999' : undefined }}>
                                {group.noNightly ? 'N/A' : `$${rate.nightly?.toFixed(2) || '0.00'}`}
                              </td>
                            </tr>
                          );
                          rowIndex++;
                        }
                      }
                      return rows;
                    })()}
                  </tbody>
                </table>
              </>
            )}

            {/* Meal Fees */}
            {settings.rates.meals && (
              <>
                <h4 style={{ color: '#2c5aa0', borderBottom: '2px solid #4299e1', paddingBottom: '0.5rem', marginBottom: '0.75rem' }}>Meal Pass Fees</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f0f4f8' }}>
                      <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem' }}>Meal Pass</th>
                      <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '0.5rem 0.75rem' }}>Full Camp Pass — Adult</td>
                      <td style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>${(settings.rates.meals.fullCampAdult || 0).toFixed(2)}</td>
                    </tr>
                    <tr style={{ backgroundColor: '#f9f9f9' }}>
                      <td style={{ padding: '0.5rem 0.75rem' }}>Full Camp Pass — Child</td>
                      <td style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>${(settings.rates.meals.fullCampChild || 0).toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '0.5rem 0.75rem' }}>Daily Pass — Adult</td>
                      <td style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>${(settings.rates.meals.dailyAdult || 0).toFixed(2)}</td>
                    </tr>
                    <tr style={{ backgroundColor: '#f9f9f9' }}>
                      <td style={{ padding: '0.5rem 0.75rem' }}>Daily Pass — Child</td>
                      <td style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>${(settings.rates.meals.dailyChild || 0).toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}

            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <button
                onClick={() => setShowFeesModal(false)}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1.5rem',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Accommodation History Modal */}
      {showAccommodationHistory && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: '#333' }}>Accommodation History</h3>
              <button
                onClick={() => setShowAccommodationHistory(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                Your accommodation history for the last 8 years:
              </p>
            </div>
            
            <div style={{ border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden' }}>
              {[0, 1, 2, 3, 4, 5, 6, 7].map((index) => {
                const currentYear = new Date().getFullYear();
                const displayYear = currentYear - index;
                const accommodation = accommodationHistory[displayYear] || accommodationHistory[displayYear.toString()] || 'Did Not Attend';
                
                return (
                  <div key={index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    borderBottom: index < 7 ? '1px solid #eee' : 'none',
                    backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white'
                  }}>
                    <span style={{ fontWeight: 'bold', color: '#333', minWidth: '60px' }}>
                      {displayYear}
                    </span>
                    <span style={{
                      color: accommodation === 'Did Not Attend' ? '#999' : '#333',
                      fontStyle: accommodation === 'Did Not Attend' ? 'italic' : 'normal'
                    }}>
                      {accommodation}
                    </span>
                  </div>
                );
              })}
            </div>
            
            <div style={{
              marginTop: '1.25rem',
              padding: '0.75rem 1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              fontSize: '0.85rem',
              color: '#555',
              lineHeight: '1.5'
            }}>
              If this history doesn't look right to you, please just make a note in the
              "Additional Information for the Registrar" box above. Your history is linked
              to your email address, so if you've changed your email it may not display
              correctly. We can adjust it later on — nothing to worry about.
            </div>

            <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
              <button
                onClick={() => setShowAccommodationHistory(false)}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1rem',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Registration;
